import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import useVisitStore from '../../stores/visitStore';
import useAppointmentStore from '../../stores/appointmentStore';
import useOrgSettingsStore from '../../stores/orgSettingsStore';
import useDepartmentStore from '../../stores/departmentStore';
import {
  resolveDepartmentPicker,
  filterProvidersByDept,
  rederiveDepartmentOnProviderChange,
  classifyRegisterError,
  isEmergencyVisitType,
} from './departmentResolution';
import { usePermission, usePermissions, useAnyPermission, useScope } from '../../context/PermissionContext';
import { useTheme } from '../../theme/ThemeContext';
import { SPECIALIZATION_LABELS, STAFF_ROLES, ASSIGNABLE_PURPOSE } from '../../constants/staff';
import { updatePatientDemographics, getPatient, fetchFeeSettings, updateVisitBilling, getRegistrationBillingPreview, mapDoctorsResponse } from '../../services/api/visitApi';
import { linkABHAToPatient, claimDiscoveredScan } from '../../services/api/abhaApi';
import { claimErrorMessage, EXISTING_PATIENT_VERIFY_NOTICE } from '../../utils/scanShare';
import { snapshotLookupMobile, abhaAutofillPatch, isLastNameRequired, isLastNameLocked, isFirstNameLocked, formatPatientName, isMaskedAbhaNumber, SELF_TAKEN_COPY, isSelfTakenError } from '../../services/api/abhaProfile';
import AbhaBadge from './AbhaBadge';
import AbhaLinkConfirmDialog from '../abha/AbhaLinkConfirmDialog';
import AbhaAlreadyLinkedNotice from '../abha/AbhaAlreadyLinkedNotice';
import useAbhaDuplicateGuard, { ABHA_ALREADY_LINKED_CODE, ABHA_ALREADY_LINKED_NOTE } from '../../hooks/useAbhaDuplicateGuard';
import ABHASection from '../abha/ABHASection';
import useAbdmEnabled from '../../hooks/useAbdmEnabled';
import { normalizeGender } from '../../utils/gender';
import MaskedAadhaarInput from '../common/MaskedAadhaarInput';
import { selectLabTechnicians } from '../../utils/labTechnicianRoster';
import { collectBundledPayment } from '../../services/api/visitActionPendingApi';
import RegistrationProceduresSection from './RegistrationProceduresSection';
import { visitTypeCapabilities } from './visitTypeCapabilities';
import RegistrationInvestigationsSection from './RegistrationInvestigationsSection';
import { resolveAdvisedBy, tokenProviderFields } from './advisedByAttribution';
import { resolvePatientIdentity } from './patientIdentity';
import AdvisedByDoctorField from './AdvisedByDoctorField';
import useLabEnabled from '../../hooks/useLabEnabled';
import useAssignableProviders, {
  DOCTORS_LOADING_COPY, DOCTORS_LOAD_ERROR_COPY, doctorsEmptyCopy, DOCTORS_SCOPED_FOOTER_COPY,
  NURSES_LOADING_COPY, NURSES_LOAD_ERROR_COPY, LAB_TECHS_LOADING_COPY, LAB_TECHS_LOAD_ERROR_COPY,
} from '../../hooks/useAssignableProviders';
import { ADMISSION_ACCESS_ERROR_CODE, PROVIDER_OUT_OF_SCOPE_COPY_BY_ROLE } from '../../constants/admission';
import { adviseLabOrder, assignLabOrder, cancelLabOrder, collectLabPaymentBundle } from '../../services/api/labApi';
import { PAYMENT_MODES } from '../../constants/visitMetadata';
import { printToken } from '../../utils/printToken';
import { fetchClinicBranding } from '../../services/api/visitApi';
import { listReceiptsForVisit, sumCollectedRupees, shouldItemizeToken, tokenLineItemsFromReceipts, tokenMoneyFromCollect, tokenReceiptNumberFromCollect, tokenReceiptNumberPrintedFromCollect } from '../../services/api/receiptsApi';
import { providerCategory, VISIT_TYPE_LABELS } from '../../constants/visitProviderCategories';
import AgeInput from '../common/AgeInput';
import AddressBlock from '../common/AddressBlock';
import { ADDRESS_FIELDS, PINCODE_ERROR, abhaAddressFill, abhaAddressValues, needsPostLinkAddressPatch, pickAddress } from '../../utils/patientAddress';
import { ageFromDob, patientAgeDisplay, serializeAgeValue } from '../../utils/ageValue';
import AdmittedElsewhereLabel from '../common/AdmittedElsewhereLabel';
import ConsentBlock, { resolveConsentGranted } from '../common/ConsentBlock';
import { DIGITAL_RECORDS_KEY } from '../../services/api/consentApi';
import { SCAN_SHARE_COPY } from '../../constants/abdmCopy';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * RegisterVisitForm — Glassmorphic, desktop-adaptive visit registration
 *
 * Supports family-member registration:
 *   Case 1: New mobile → new patient form (relation = SELF by default)
 *   Case 2: Known mobile → show linked patients to confirm who is visiting
 *   Case 3: Known mobile → "Add Family Member" to register someone new
 *
 * Desktop: fills parent container (admin-module-form 640-720px)
 * Mobile: full-width with proper padding
 *
 * Backend: POST /api/organisation/visits
 * Response: { patient, visit, queue_number, is_new_patient }
 * ═══════════════════════════════════════════════════════════════════════
 */

const VISIT_TYPES = [
  { value: 'REGULAR', label: 'Regular', icon: '🏥' },
  { value: 'EMERGENCY', label: 'Emergency', icon: '🚨' },
  { value: 'FOLLOW_UP', label: 'Follow Up', icon: '🔄' },
  { value: 'REPORT_CHECKUP', label: 'Report Checkup', icon: '📋' },
];

// OTC (W3) — an OTC visit is attended by EXACTLY ONE of a doctor OR a nurse.
// The attendee-kind sub-toggle options + the "neither picked" validation string.
// NEW user-visible strings (flagged for owner ratification at handoff).
const OTC_ATTENDEE_KINDS = [
  { value: 'DOCTOR', label: 'Doctor', icon: '🩺' },
  { value: 'NURSE', label: 'Nurse', icon: '⚕' },
];
const OTC_ATTENDEE_REQUIRED = 'Select a doctor or a nurse for this OTC visit';
// OR-127 — caption under the read-only age when it is calculated from the DOB (mockup copy).
const AGE_FROM_DOB_NOTE = 'Calculated from date of birth';

// Exported so other patient-demographic surfaces (e.g. the C1 admit desk's
// inline new-patient dialog) reuse the SAME relation vocabulary instead of a
// parallel copy.
export const RELATIONS = [
  { value: 'SELF', label: 'Self' },
  { value: 'WIFE', label: 'Wife' },
  { value: 'HUSBAND', label: 'Husband' },
  { value: 'CHILD', label: 'Child' },
  { value: 'PARENT', label: 'Parent' },
  { value: 'FRIEND', label: 'Friend' },
  { value: 'OTHER', label: 'Other' },
];

// BUSY-VOUCHER R1 — "Advised by doctor" empty state (picker mode, nothing
// picked). Spread into INITIAL_FORM and into the provider-switch clear.
const NO_ADVISED_BY = { advisedByDoctorId: '', advisedByTypedName: null };

// OR-43/OR-55 — "Lab & Diagnostic Visit", single-sourced from the provider
// category (toggle, technician's static line, submit button).
const LAB_VISIT_LABEL = `${providerCategory('LAB').label} Visit`;

// ── Structured address (OR-122..OR-136) ─────────────────────────────────
// The form carries the six `address_*` fields only. Never `address_source`
// (server-written, read-only) and never the legacy one-line `address`:
// pickAddress() seeds an old one-line address into `address_line` (OR-128).
const PINCODE_RE = /^[0-9]{6}$/;
const addressOf = (src) => Object.fromEntries(ADDRESS_FIELDS.map((f) => [f, String(src?.[f] ?? '')]));
const EMPTY_ADDRESS = addressOf(null);
// Existing patient: the visit registration must not re-send the six (address
// edits go through the Edit PATCH). `undefined` = key omitted by visitApi.
const NO_ADDRESS_KEYS = Object.fromEntries(ADDRESS_FIELDS.map((f) => [f, undefined]));
const NO_SOURCE = {}; // stable identity for AddressBlock's `source` prop

// OR-121: an OTP ABHA address REPLACES what was typed (all six; a part ABHA left
// empty clears). OR-137: a Scan & Share address fills only an EMPTY address.
// Nothing is replaced when ABHA has no address (null).
const abhaFormPatch = (snapshot, form) => ({
  ...abhaAutofillPatch(snapshot, form),
  ...(abhaAddressFill(snapshot, form) || {}),
});

// OR-127 / D9: a known DOB wins — age is derived and (when sent) sent as the
// derived string; the typed age only counts while there is no usable DOB.
const effectiveAge = (f) => {
  const derived = ageFromDob(f.dob);
  return derived ? serializeAgeValue(derived.amount, derived.unit) : f.age;
};

// The patient-derived slice of the form (select + post-link refresh).
const patientFormFields = (patient) => ({
  patient_id: patient.patient_id,
  first_name: patient.first_name || '',
  last_name: patient.last_name || '',
  relation: patient.relation || 'SELF',
  age: patient.age || '',
  dob: patient.dob || '',
  // The patient record's gender is stored loosely (PatientResponse.gender
  // is a plain str, not the enum) — a legacy record can carry an
  // abbreviated code ("M"/"F"/"O") from before the registration schema
  // enforced MALE/FEMALE/OTHER. Re-submitting that raw value 422s on
  // register (PatientRegistrationRequest.gender IS the strict enum), so
  // normalize it going into the form the same way an ABHA snapshot's
  // M/F/O code is normalized. An already-canonical or unknown value
  // passes through / drops to '' respectively.
  gender: normalizeGender(patient.gender),
  aadhaar_no: patient.aadhaar_no || '',
  ...addressOf(pickAddress(patient)),
  allergies: patient.allergies || '',
});

const INITIAL_FORM = {
  patient_id: '',
  first_name: '',
  last_name: '',
  mobile: '',
  visitProviderType: 'DOCTOR',
  // OTC (W3) — which single attendee kind an OTC visit uses: 'DOCTOR' (default)
  // or 'NURSE'. Only meaningful when visitProviderType === 'NURSE'; the
  // unselected id is always cleared.
  otcAttendeeKind: 'DOCTOR',
  visitDoctorId: '',
  visitNurseId: '',
  visitLabTechId: '',
  visitType: 'REGULAR',
  relation: 'SELF',
  age: '',
  dob: '',
  gender: '',
  aadhaar_no: '',
  birth_certificate: '',
  ...EMPTY_ADDRESS,
  // rx-allergies P2 — patient-record allergies captured at reception
  // (Business condition 4: a receptionist-entered value is an acceptable
  // initial value — no verified flag, no approval step).
  allergies: '',
  appointment_id: '',
  paymentMode: '',
  abha_number: '',
  abha_address: '',
  // A2 — dept-of-care stamped at registration (frozen). '' = not chosen yet.
  department_id: '',
  // CONSENT-CAPTURE P1 (D6/D13) — re-derived by ONE effect below from
  // (existing patient record, server catalogue default) whenever the
  // patient context or the catalogue changes; this literal is only the
  // pre-render placeholder (the visible checkbox stays behind a skeleton
  // until the catalogue resolves — see ConsentBlock).
  consentGranted: true,
  // BUSY-VOUCHER R1 — optional "Advised by doctor" (Lab & Diagnostic visit
  // only): a picked hospital doctor (member_id) OR a typed outside name
  // (null = picker mode, string = typed mode). Lives in the form so every
  // `setForm(INITIAL_FORM)` reset clears it.
  ...NO_ADVISED_BY,
};

// ─────────────────────────────────────────────────────────────────────────────

export default function RegisterVisitForm({ onSuccess, onCancel, initialAbhaSnapshot = null }) {
  const canCreate = usePermission('CREATE_VISIT');
  const { show: abdmOn } = useAbdmEnabled();
  const canScanShare = usePermission('ABHA_SCAN_SHARE') && abdmOn; // R131: no claim call without it (server 403s)
  const { role, memberId: viewerMemberId } = usePermissions();
  // LAB-ASSIGN-2 / FE-3 — the lab technician's Register Visit is locked to a
  // Lab visit: no control on this screen can reach a Doctor/Nurse visit.
  const isLabTechnician = (role || '').toUpperCase() === STAFF_ROLES.LAB_TECHNICIAN;
  // Read fresh via getState() in handleSubmit (after the lazy fetch) rather than
  // subscribing, so the gate sees the loaded value without an extra re-render.
  const fetchProcedurePaymentSettings = useOrgSettingsStore((s) => s.fetchProcedurePaymentSettings);
  // CONSENT-CAPTURE P1 — server-owned catalogue (text/version/default),
  // single-flight cached in orgSettingsStore (mirrors fetchProcedurePaymentSettings).
  const consentCatalog = useOrgSettingsStore((s) => s.consentCatalog);
  const fetchConsentCatalog = useOrgSettingsStore((s) => s.fetchConsentCatalog);
  const { isDark } = useTheme();
  const {
    doctors, patientLookup, loading, error, createdVisit,
    fetchDoctors, searchPatient, clearPatientLookup, registerVisit,
    clearError, setError, clearCreatedVisit,
  } = useVisitStore();

  const { detectByMobile, detectedAppointment, clearDetected } = useAppointmentStore();

  // ── Departments (EPIC A / A2, S7) — REUSE the A1 departmentStore (single
  // source of truth; never refetch the list inline). The dept-of-care is
  // stamped at registration when the org has departments and derives from the
  // SELECTED ATTENDING DOCTOR (NOT the logged-in user): `deptsByMember[doctorId]`
  // drives auto-lock (1 dept) / constrain (multi) / full-list (no dept). The
  // reverse filter uses the whole `deptsByMember` map to narrow the doctor list
  // once a department is chosen first.
  const departments = useDepartmentStore((s) => s.departments);
  const deptsLoaded = useDepartmentStore((s) => s.deptsLoaded);
  const fetchDepartments = useDepartmentStore((s) => s.fetchDepartments);
  const fetchMemberDepts = useDepartmentStore((s) => s.fetchMemberDepts);
  const deptsByMember = useDepartmentStore((s) => s.deptsByMember);

  const [form, setForm] = useState(() => ({
    ...INITIAL_FORM,
    ...(isLabTechnician ? { visitProviderType: 'LAB' } : null),
    abha_number: initialAbhaSnapshot?.abhaNumber || '',
    abha_address: initialAbhaSnapshot?.abhaAddress || '',
  }));
  const formRef = useRef(form);
  formRef.current = form;
  // AddressBlock props — memoized so the block never sees a new identity unless a
  // field really changed (no re-render churn, no effect re-fires downstream).
  const {
    address_line: aLine, address_town: aTown, address_sub_district: aSubDistrict,
    address_district: aDistrict, address_state: aState, address_pincode: aPincode,
  } = form;
  const addressValue = useMemo(
    () => ({
      address_line: aLine, address_town: aTown, address_sub_district: aSubDistrict,
      address_district: aDistrict, address_state: aState, address_pincode: aPincode,
    }),
    [aLine, aTown, aSubDistrict, aDistrict, aState, aPincode],
  );
  const handleAddressChange = useCallback((next) => {
    setForm((f) => ({ ...f, ...addressOf(next) }));
    setValidationErrors((prev) => (prev.address_pincode ? { ...prev, address_pincode: undefined } : prev));
  }, []);
  const [view, setView] = useState('mobile');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [blockError, setBlockError] = useState('');
  const abhaDup = useAbhaDuplicateGuard();
  const clearAbhaDup = abhaDup.clear;
  const [appointmentLinked, setAppointmentLinked] = useState(false);
  const [expiredDismissed, setExpiredDismissed] = useState(false); // user chose "Continue as Walk-In"
  const [editSaving, setEditSaving] = useState(false);
  const [branding, setBranding] = useState({ name: 'Clinic', description: '', address: '', pincode: '', phone: '' });
  const [orgFeeSettings, setOrgFeeSettings] = useState(null);
  const labEnabled = useLabEnabled();
  // Ordering a lab test (advise) needs ORDER_LAB or ACCESS_IPD — the same
  // alternatives the server accepts at organisation_lab.py:112 (LAB-ASSIGN-2 /
  // FE-3) — so the walk-in investigations picker shows to anyone who can
  // actually create the order (else the advise call 403s).
  const canOrderLab = useAnyPermission('ORDER_LAB', 'ACCESS_IPD');
  const [consultationFee, setConsultationFee] = useState('');
  const [registrationFee, setRegistrationFee] = useState('');
  // TOKEN-UPDATE T1 FE5 — server-sourced total collected for the SuccessPopup's
  // printed Amount Paid row. Computed AFTER the lab/procedure collect calls
  // below finish (never on popup mount) so it can't race those awaits and
  // under-count (GATE 3 RULING 2).
  const [registrationAmountPaid, setRegistrationAmountPaid] = useState(null);
  // Issue-1 — per-scan line items for the same receipts fetch above, so a
  // 2+-investigation lab visit's token itemizes instead of one summed row.
  const [registrationLineItems, setRegistrationLineItems] = useState(null);
  // F16/W7 — the minted Receipt No. for the SuccessPopup's token. Post-W7 the
  // register response's `visit.receipt_number` is NULL (numbers mint at
  // PAYMENT, not registration), so it's folded here from the collect RESPONSE
  // the client already holds (tokenReceiptNumberFromCollect). Null for an
  // unpaid registration → the token omits the Receipt No. row.
  const [registrationReceiptNumber, setRegistrationReceiptNumber] = useState(null);
  // GST-DOCNO/A2-d (R-GD-10) — the printed sibling of the above, folded from
  // the SAME collect-response source (tokenReceiptNumberPrintedFromCollect).
  // Null for an unpaid registration or a pre-change/older-backend payment —
  // the token then falls back to the bare `registrationReceiptNumber` int,
  // byte-identical to today.
  const [registrationReceiptNumberPrinted, setRegistrationReceiptNumberPrinted] = useState(null);
  // F2 — the submitted department's display name for the SuccessPopup's
  // printed token. Captured right after submit (form.department_id is still
  // the value that was just registered) because `setForm(INITIAL_FORM)` at
  // the end of handleSubmit clears it before this popup renders.
  const [registrationDepartmentName, setRegistrationDepartmentName] = useState(null);
  // F13 (2026-08-02) — TRUE only while an in-flight registration is still
  // deriving the token's money (between registerVisit resolving — which mounts
  // the SuccessPopup — and the collect responses producing the amount/line
  // items). Gates the "Print Token" button so a fast click can't print an
  // empty/wrong token before the money is ready. Scoped to a live submit (NOT a
  // "ready" latch) so an injected / store-persisted createdVisit with no submit
  // in flight stays printable and a remount can't strand the button.
  const [tokenPreparing, setTokenPreparing] = useState(false);
  // Procedures-on-New-Visits — selected procedure lines (sitting_moves) + their
  // folded fee, plus the free-follow-up waiver note for the consultation fee.
  const [procedureLines, setProcedureLines] = useState([]);
  const [procedureFeeTotal, setProcedureFeeTotal] = useState(0);
  // Lab Visit — selected investigations (lab tests) the front desk is ordering.
  // Each { procedure_id, name, cost, skip_payment }; turned into LabOrders on submit.
  const [investigationLines, setInvestigationLines] = useState([]);
  const [feeWaiverNote, setFeeWaiverNote] = useState('');
  // Free-follow-up preview for the selected patient+doctor: { base, effective,
  // waived, reason }. Drives the Type↔fee coupling — when the patient is
  // waiver-eligible, Type=Follow-up waives the consultation fee, any other
  // Type charges the base fee.
  const [feePreview, setFeePreview] = useState(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 600);
  // abhaResult: no longer set by an in-form ABHA widget (removed) — only ever seeded via
  // initialAbhaSnapshot (deep-link from the ABHA page), which the submit-time link below still
  // honours so that entry path keeps auto-linking exactly as before.
  const [abhaResult, setAbhaResult] = useState(initialAbhaSnapshot);
  // What the server will write as the address at link time (null = ABHA has none).
  const abhaAddress = useMemo(() => abhaAddressValues(abhaResult), [abhaResult]);
  const [scanLinkNote, setScanLinkNote] = useState('');
  // The ABHA result from the create/verify flow prefills the EMPTY new-patient fields once
  // (never overwrites typed values, never touches the mobile the user entered).
  // "Is this the same person?" gate: set when a verified ABHA is waiting and staff pick an EXISTING
  // patient — nothing links to that record until the dialog confirms it.
  const [linkConfirmPatient, setLinkConfirmPatient] = useState(null);
  const [scanNotUsedNote, setScanNotUsedNote] = useState(false);
  const [relationHint, setRelationHint] = useState(false);   // number already has a SELF patient → Relation must be chosen
  const [abhaOldUhid, setAbhaOldUhid] = useState('');   // old record left unchanged by "Register as new patient"
  const abhaPrefillAppliedRef = useRef(false);
  useEffect(() => {
    if (!initialAbhaSnapshot || abhaPrefillAppliedRef.current) return;
    abhaPrefillAppliedRef.current = true;
    setForm((f) => ({ ...f, ...abhaFormPatch(initialAbhaSnapshot, f) }));
  }, [initialAbhaSnapshot]);
  const debounceRef = useRef(null);
  // S7/S8 — remembers the previous ACTIVE-PROVIDER id (drives dept re-derivation
  // when the active provider changes: a doctor/nurse/lab-tech pick OR a visit-type
  // switch that changes which provider is active) and whether the current
  // department was a MANUAL pick made with NO provider set (drives AC7 "clear
  // provider → keep manual pick" retention).
  const prevProviderRef = useRef('');
  const manualDeptPickRef = useRef(false);
  // S7/S8 — tracks which SELECTED providers we've already refreshed from the
  // AUTHORITATIVE per-member endpoint (MEMBER→DEPT). deptsByMember can be
  // pre-populated by the Manage-Staff bulk hydrate (hydrateAllMemberDepts),
  // which INVERTS the dept→member side and can be stale/disagree with the
  // member→dept edges the SERVER validates the visit's department against. So
  // for the selected provider we always fetch the authoritative edges once
  // (cached after) instead of trusting a possibly-stale bulk value — the
  // auto-fill/lock then matches exactly what the server will accept.
  const refreshedProvidersRef = useRef(new Set());
  // F16 — the visit_id of the token currently owning the SuccessPopup's money
  // state. The reconcile read-back is DETACHED (fire-and-forget) so submit
  // settles the instant collect-derived money lands; when the detached read
  // resolves LATE (after the popup closed or a NEXT registration started) it
  // must be a no-op — it compares against this ref and only upgrades state that
  // still belongs to its own submit (no stale-visit leakage, no setState for a
  // token that's no longer shown).
  const activeTokenVisitRef = useRef(null);

  // ── ACTIVE ATTENDING PROVIDER (EPIC A / A2, S8) — the single provider the
  // visit's department keys on, chosen by the visit type: the DOCTOR for an OPD
  // (Doctor) visit, the NURSE for a Nurse visit, the LAB TECHNICIAN for a Lab
  // visit. Exactly one is set at a time (the provider-switch clears the others).
  // Every dept-linkage consumer below (hydration, resolution, re-derivation,
  // reverse filter) reads THIS id/list instead of hardcoding the doctor, so the
  // S7 behaviour applies uniformly to all three roles from ONE source.
  // OTC (W3) — an OTC (visit_type NURSE) visit whose single attendee is a DOCTOR.
  // The visit type stays NURSE (queue sectioning, consult-fee suppression, labels
  // all still key off NURSE); only the ATTENDING provider becomes the doctor, so
  // every dept-linkage consumer below reads the doctor id/list for this leg.
  const isOtcDoctor = form.visitProviderType === 'NURSE' && form.otcAttendeeKind === 'DOCTOR';
  const activeProviderId = form.visitProviderType === 'NURSE'
    ? (isOtcDoctor ? form.visitDoctorId : form.visitNurseId)
    : form.visitProviderType === 'LAB'
      ? form.visitLabTechId
      : form.visitDoctorId;
  // ── PICKER lists (admission-access S10) — served SCOPED by the server
  // (GET /providers/assignable?role=…): the caller's departments' providers +
  // no-department providers; ADMIN / unassigned caller → everyone. The client
  // never re-filters by the viewer's departments and never falls back to the
  // org-wide roster while a list loads. One request per role per session
  // (single-flight, cached in staffApi); only the role of the picker that is
  // on screen is requested. `doctors` (visitStore) stays the org-wide NAME
  // source (availability warning, receipt/token doctor name) — NOT a picker.
  const wantsDoctors = form.visitProviderType === 'DOCTOR' || isOtcDoctor;
  const wantsNurses = form.visitProviderType === 'NURSE' && !isOtcDoctor;
  const wantsLabTechs = form.visitProviderType === 'LAB';
  const { isScoped, isAdmin: viewerIsAdmin, viewerDeptIds } = useScope();
  // Display-only: the picker footer says the list is narrowed. The narrowing
  // itself happens on the server (see the PICKER lists above).
  const viewerIsDeptScoped = !viewerIsAdmin && Array.isArray(viewerDeptIds) && viewerDeptIds.length > 0;
  // R2 / bk-1733 — an EMERGENCY visit is org-wide (the server exempts it from the
  // provider wall), so a walled viewer's doctor picker asks for the org-wide roster
  // (`?purpose=emergency`). Admin / unassigned viewers already get it by default:
  // no extra request. `undefined` keeps the default (scoped) call.
  const emergencyVisit = isEmergencyVisitType(form.visitType);
  const doctorPickerIsWalled = viewerIsDeptScoped && !emergencyVisit;
  const { providers: assignableDoctors, loading: doctorsLoading, error: doctorsError } =
    useAssignableProviders(
      wantsDoctors ? STAFF_ROLES.DOCTOR : null,
      viewerIsDeptScoped && emergencyVisit ? ASSIGNABLE_PURPOSE.EMERGENCY : undefined,
    );
  const { providers: viewerNurses, loading: nursesLoading, error: nursesError } =
    useAssignableProviders(wantsNurses ? STAFF_ROLES.NURSE : null);
  const { providers: assignableLabTechs, loading: labTechsLoading, error: labTechsError } =
    useAssignableProviders(wantsLabTechs ? STAFF_ROLES.LAB_TECHNICIAN : null);
  // Same item mapping + INACTIVE/DISABLED filter the doctors slice always used
  // (mapDoctorsResponse), so the option shape is unchanged.
  const viewerDoctors = useMemo(() => mapDoctorsResponse(assignableDoctors), [assignableDoctors]);
  // LAB-ASSIGN/T3 — single source of truth for "what counts as a lab technician"
  // (shared with OrderLabDialog/LabAssignPanel): role + ACTIVE filter, name sort.
  const viewerLabTechs = useMemo(() => selectLabTechnicians(assignableLabTechs), [assignableLabTechs]);
  const activeProviderList = form.visitProviderType === 'NURSE'
    ? (isOtcDoctor ? viewerDoctors : viewerNurses)
    : form.visitProviderType === 'LAB'
      ? viewerLabTechs
      : viewerDoctors;

  useEffect(() => { fetchDoctors(); }, [fetchDoctors]);
  // A2 S7 — load the org dept list once (only if not already cached by A1 UI).
  useEffect(() => { if (!deptsLoaded) fetchDepartments(); }, [deptsLoaded, fetchDepartments]);
  // S7/S8 — the dept-of-care derives from the SELECTED ACTIVE PROVIDER (doctor
  // OPD / nurse / lab tech), so hydrate THAT provider's dept edges
  // (deptsByMember[providerId]) on pick, only if not cached. Nurses/lab techs are
  // org members keyed the same way as doctors.
  useEffect(() => {
    const pid = activeProviderId;
    if (!pid) return;
    // Refresh the SELECTED provider's edges from the authoritative per-member
    // endpoint ONCE per provider — even when deptsByMember[pid] is already set
    // (a bulk-inverted / stale value from Manage-Staff must not be trusted for
    // the dept the server validates). Cached after the first fetch per provider.
    if (!refreshedProvidersRef.current.has(pid)) {
      refreshedProvidersRef.current.add(pid);
      fetchMemberDepts(pid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProviderId, fetchMemberDepts]);
  // S7/S8 reverse filter (AC5) — when a department is picked FIRST, hydrate every
  // ACTIVE-PROVIDER candidate's edges so the provider dropdown (doctors OPD /
  // nurses / lab techs) can narrow to that department's members. One-time per
  // member; deptsByMember caches results.
  useEffect(() => {
    if (!form.department_id || activeProviderId) return;
    for (const p of activeProviderList) {
      const id = p.member_id || p.doctor_id;
      if (id && deptsByMember[id] === undefined) fetchMemberDepts(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.department_id, activeProviderId, activeProviderList, deptsByMember, fetchMemberDepts]);
  useEffect(() => { fetchClinicBranding().then(setBranding).catch(() => {}); }, []);
  useEffect(() => { fetchFeeSettings().then(setOrgFeeSettings).catch(() => {}); }, []);
  useEffect(() => { fetchProcedurePaymentSettings(); }, [fetchProcedurePaymentSettings]);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // ── Department picker resolution (EPIC A / A2, S7 + S8) — ONE source of truth
  // for the show / required / locked / options / auto-fill decision, derived from
  // the SELECTED ACTIVE ATTENDING PROVIDER's depts (doctor OPD / nurse / lab tech;
  // NOT the logged-in user). Consumed by the render gate, the re-derivation
  // effect, the provider filter AND the submit validation so they can't drift.
  // Emergency visits are org-wide (server stamps the EMERGENCY sentinel) → no
  // picker. No provider chosen yet → hasActiveProvider false → full org list,
  // manual pick.
  const hasActiveProvider = !!activeProviderId;
  const activeProviderDepts = activeProviderId ? deptsByMember[activeProviderId] : null;
  const deptResolution = useMemo(
    () => resolveDepartmentPicker({
      departments,
      activeProviderDepts,
      hasActiveProvider,
      visitType: form.visitType,
    }),
    [departments, activeProviderDepts, hasActiveProvider, form.visitType],
  );

  // ── Re-derive the dept-of-care whenever the ACTIVE PROVIDER changes (AC2, AC6,
  // AC7) — a doctor/nurse/lab-tech pick OR a visit-type switch that changes which
  // provider is active. single→reset+lock, multi→keep-if-valid-else-clear,
  // no-dept→keep manual-if-valid-else-clear, cleared→drop auto / retain manual. A
  // dept picked with NO provider is a MANUAL pick (retained on clear); once
  // auto-derived from a provider it is not. The single source
  // (rederiveDepartmentOnProviderChange) guarantees we never keep a dept the new
  // provider isn't a member of. The derivation key folds in the provider TYPE so
  // switching visit type (doctor→nurse→lab) always counts as a provider change.
  const providerForDerivation = hasActiveProvider
    ? `${form.visitProviderType}:${activeProviderId}`
    : '';
  const lockedAutoFill = deptResolution.locked ? deptResolution.autoFillDeptId : null;
  useEffect(() => {
    const prev = prevProviderRef.current;
    const providerChanged = prev !== providerForDerivation;
    // Re-derive on a real provider change; ALSO apply a single-dept auto-lock that
    // only becomes known after the provider's edges hydrate async (same provider,
    // resolution flips to locked) so AC2 isn't missed on the initial pick.
    const lockPending = !providerChanged && lockedAutoFill && form.department_id !== lockedAutoFill;
    if (!providerChanged && !lockPending) return;
    const providerCleared = !providerForDerivation && !!prev;
    prevProviderRef.current = providerForDerivation;
    setForm((f) => {
      const next = rederiveDepartmentOnProviderChange({
        resolution: deptResolution,
        currentDeptId: f.department_id,
        providerCleared,
        wasManualPick: manualDeptPickRef.current,
      });
      // A value derived from a provider is no longer a manual pick; a retained
      // manual pick stays manual.
      manualDeptPickRef.current = providerCleared ? manualDeptPickRef.current : false;
      return next === f.department_id ? f : { ...f, department_id: next };
    });
    // deptResolution is recomputed synchronously from the same provider id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerForDerivation, deptResolution, lockedAutoFill]);

  // Clear a stale dept selection when the picker stops being shown (e.g. the
  // user flips the visit type to EMERGENCY, or the org has no departments) so a
  // hidden value can't be submitted against the current rules.
  const showDeptPicker = deptResolution.showPicker;
  useEffect(() => {
    if (!showDeptPicker) {
      manualDeptPickRef.current = false;
      setForm((f) => (f.department_id ? { ...f, department_id: '' } : f));
    }
  }, [showDeptPicker]);

  // Leaving EMERGENCY puts the wall back (the hook refetches the scoped list). A
  // doctor picked from the org-wide list may not be in it: once the scoped list
  // has settled, drop a pick it does not contain, so the select never looks empty
  // while the hidden pick still submits (server: 403 PROVIDER_OUT_OF_SCOPE). Only
  // on that transition — other prefills (appointment, auto-assign) are untouched.
  const prevEmergencyRef = useRef(emergencyVisit);
  const recheckDoctorPickRef = useRef(false);
  useEffect(() => {
    if (prevEmergencyRef.current !== emergencyVisit) {
      recheckDoctorPickRef.current = prevEmergencyRef.current && viewerIsDeptScoped;
      prevEmergencyRef.current = emergencyVisit;
    }
    if (!recheckDoctorPickRef.current || doctorsLoading || doctorsError) return;
    recheckDoctorPickRef.current = false;
    setForm((f) => (f.visitDoctorId && !viewerDoctors.some((d) => d.doctor_id === f.visitDoctorId)
      ? { ...f, visitDoctorId: '' }
      : f));
  }, [emergencyVisit, viewerIsDeptScoped, doctorsLoading, doctorsError, viewerDoctors]);

  // ACTIVE-PROVIDER list filtered by a department picked FIRST (AC5, S7 + S8) —
  // reuses the shared deptsByMember map (single source; no inline re-derivation).
  // When a dept is chosen before a provider, the active dropdown (doctors OPD /
  // nurses / lab techs) narrows to that dept's members. No dept chosen → full
  // list. One filter serves all three roles. Applied ON TOP of the server-scoped
  // picker lists.
  const filteredProviders = useMemo(
    () => filterProvidersByDept(activeProviderList, form.department_id, deptsByMember),
    [activeProviderList, form.department_id, deptsByMember],
  );
  // Role-specific views onto the filtered active list (the JSX renders the one
  // that matches the current provider type).
  // OTC-doctor uses the doctor dropdown as the ACTIVE list → dept-filter it like
  // nurses; OTC-nurse keeps the nurse dropdown as the active list.
  const filteredDoctors = (form.visitProviderType === 'DOCTOR' || isOtcDoctor) ? filteredProviders : viewerDoctors;
  const filteredNurses = (form.visitProviderType === 'NURSE' && !isOtcDoctor) ? filteredProviders : viewerNurses;
  const filteredLabTechs = form.visitProviderType === 'LAB' ? filteredProviders : viewerLabTechs;

  // ── Auto-assign doctor when only one is available ──
  // OTC and Lab Visit are doctor-less — the backend forbids
  // visit_doctor_id on them — so only auto-assign for a Doctor (OPD) visit.
  const visitProviderType = form.visitProviderType;
  useEffect(() => {
    if (visitProviderType !== 'DOCTOR') return;
    // Auto-assign only when the registrar's VIEWER-scoped list has exactly one
    // doctor — never auto-pick a doctor that's filtered out of their dropdown.
    if (viewerDoctors.length === 1) {
      setForm((f) => f.visitDoctorId ? f : { ...f, visitDoctorId: viewerDoctors[0].doctor_id });
    }
  }, [viewerDoctors, visitProviderType]);

  // ── Self pre-select the attending technician (LAB-ASSIGN-2 / FE-3, §10.6) ──
  // A technician registering their own walk-in Lab Visit starts pre-filled as
  // the attending technician whenever their own id is in the scoped roster —
  // editable, and it never clobbers a manual pick (guarded on "not already set").
  useEffect(() => {
    if (visitProviderType !== 'LAB' || !isLabTechnician || !viewerMemberId) return;
    if (!filteredLabTechs.some((t) => t.member_id === viewerMemberId)) return;
    setForm((f) => (f.visitLabTechId ? f : { ...f, visitLabTechId: viewerMemberId }));
  }, [visitProviderType, isLabTechnician, viewerMemberId, filteredLabTechs]);

  // ── Pre-fill consultation fee when doctor changes ──
  const visitDoctorId = form.visitDoctorId;
  const patientId = form.patient_id;
  useEffect(() => {
    // OTC / Lab Visit are doctor-less and have no consultation fee —
    // never prefill / preview it for them.
    if (visitProviderType !== 'DOCTOR') {
      setFeePreview(null);
      setFeeWaiverNote('');
      setConsultationFee('');
      return undefined;
    }
    if (!orgFeeSettings || !visitDoctorId) return;
    const base = orgFeeSettings.doctor_fees?.[visitDoctorId];
    // Returning patient → ask the backend for the free-follow-up-aware fee so
    // a re-registration within the window pre-fills a waived (0) consultation
    // and auto-selects the Follow-up visit type.
    if (patientId) {
      let cancelled = false;
      setFeeWaiverNote('');
      getRegistrationBillingPreview(patientId, visitDoctorId)
        .then((p) => {
          if (cancelled || !p) return;
          const preview = {
            base: p.base_consultation_fee != null
              ? Number(p.base_consultation_fee)
              : (base != null ? Number(base) : null),
            effective: p.effective_consultation_fee != null
              ? Number(p.effective_consultation_fee) : null,
            waived: !!p.consultation_fee_waived,
            reason: p.waiver_reason || 'Free follow-up',
          };
          setFeePreview(preview);
          if (preview.waived) {
            // Auto-select Follow-up and waive the consultation fee. The
            // receptionist can switch the Type, which recomputes the fee.
            setForm((f) => ({ ...f, visitType: 'FOLLOW_UP' }));
            setConsultationFee(preview.effective != null ? String(preview.effective) : '0');
            setFeeWaiverNote(preview.reason);
          } else {
            setConsultationFee(preview.base != null ? String(preview.base) : '');
            setFeeWaiverNote('');
          }
        })
        .catch(() => {
          if (!cancelled) {
            setFeePreview(null);
            setConsultationFee(base != null ? String(base) : '');
          }
        });
      return () => { cancelled = true; };
    }
    setFeePreview(null);
    setFeeWaiverNote('');
    setConsultationFee(base != null ? String(base) : '');
    return undefined;
  }, [visitDoctorId, orgFeeSettings, patientId, visitProviderType]);

  // ── Visit-type change: when the patient is waiver-eligible, the consultation
  //    fee follows the Type — Follow-up waives (0), any other type charges the
  //    base fee. (Plain closure, not a hook — safe to call from JSX.)
  const handleVisitTypeChange = (newType) => {
    setForm((f) => ({ ...f, visitType: newType }));
    if (feePreview && feePreview.waived) {
      if (newType === 'FOLLOW_UP') {
        setConsultationFee(feePreview.effective != null ? String(feePreview.effective) : '0');
        setFeeWaiverNote(feePreview.reason);
      } else {
        setConsultationFee(feePreview.base != null ? String(feePreview.base) : '');
        setFeeWaiverNote('');
      }
    }
  };

  // ── Provider toggle: Doctor (OPD) ↔ OTC ↔ Lab Visit ──
  // Switching clears the other providers' ids so the registration payload never
  // carries more than one (the backend forbids visit_doctor_id on a nurse/lab
  // visit). The doctor-less modes force the visit type back to REGULAR
  // (registerVisit stamps visit_type=NURSE / LAB, and the doctor-only type chips
  // are hidden for them).
  const handleProviderChange = (provider) => {
    // Clear-on-switch: drop any selection the NEW provider type can't bill, so a
    // stale pick from the previous type can never be submitted (the blunder
    // guard). Single-sourced via visitTypeCapabilities, same rule the render
    // gates + submit guards read.
    const caps = visitTypeCapabilities(provider);
    if (!caps.procedures) {
      setProcedureLines([]);
      setProcedureFeeTotal(0);
    }
    if (!caps.lab) {
      setInvestigationLines([]);
    }
    if (!caps.consultFee) {
      setConsultationFee('');
    }
    setForm((f) => {
      if (provider === 'NURSE') {
        // OTC defaults to the Doctor attendee kind. BOTH ids are cleared on
        // entry — never carry over a stale doctor pick from the OPD leg (e.g.
        // the single-doctor auto-assign below), so the registrar must always
        // make an explicit attendee choice, same as the prior default.
        return { ...f, visitProviderType: 'NURSE', otcAttendeeKind: 'DOCTOR', visitDoctorId: '', visitNurseId: '', visitLabTechId: '', visitType: 'REGULAR', ...NO_ADVISED_BY };
      }
      if (provider === 'LAB') {
        return { ...f, visitProviderType: 'LAB', visitDoctorId: '', visitNurseId: '', visitType: 'REGULAR' };
      }
      return { ...f, visitProviderType: 'DOCTOR', visitNurseId: '', visitLabTechId: '', ...NO_ADVISED_BY };
    });
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.visitDoctorId;
      delete next.visitNurseId;
      delete next.visitLabTechId;
      delete next.otcAttendee;
      return next;
    });
  };

  // ── OTC attendee-kind sub-toggle: Nurse ↔ Doctor (W3 XOR) ──
  // Exactly one attendee is set at a time; switching kind CLEARS the other id so
  // the payload can never carry both. `visit_type` stays NURSE (no consult fee,
  // OTC queue section) regardless of the kind.
  const handleOtcAttendeeKind = (kind) => {
    setForm((f) => (kind === 'DOCTOR'
      ? { ...f, otcAttendeeKind: 'DOCTOR', visitNurseId: '' }
      : { ...f, otcAttendeeKind: 'NURSE', visitDoctorId: '' }));
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.visitDoctorId;
      delete next.visitNurseId;
      delete next.otcAttendee;
      return next;
    });
  };

  // ── Pre-fill registration fee for new patients ──
  useEffect(() => {
    if (!orgFeeSettings?.registration_fee_enabled || patientId) {
      setRegistrationFee('');
      return;
    }
    if (orgFeeSettings.registration_fee != null) {
      setRegistrationFee(String(orgFeeSettings.registration_fee));
    }
  }, [patientId, orgFeeSettings]);

  // ── Clear store state on unmount so stale data doesn't stick ──
  useEffect(() => {
    return () => {
      clearPatientLookup();
      clearError();
      clearDetected();
    };
  }, [clearPatientLookup, clearError, clearDetected]);

  // CONSENT-CAPTURE P1 — fetch the catalogue once (store-level single-flight;
  // safe to call from every mount, including AdmitPatientForm's own call).
  useEffect(() => {
    fetchConsentCatalog?.();
  }, [fetchConsentCatalog]);

  // Single source of truth (CLAUDE.md rule) for the consent checkbox's
  // derived state: existing patient record wins, else the server catalogue
  // default. Re-fires whenever the patient context changes (select / add
  // family member / new patient) or the catalogue finishes loading — so a
  // switched patient's box always re-fills instead of carrying over a stale
  // tick (S2 AC, "no tick from the previous patient survives").
  const consentCatalogEntry = (consentCatalog?.consents || []).find((c) => c.key === DIGITAL_RECORDS_KEY) || null;
  const digitalRecordsEntry = selectedPatient?.consents?.[DIGITAL_RECORDS_KEY] || null;
  // resolveConsentGranted only ever reads these two primitives (existingEntry.granted,
  // catalogEntry.default_granted) — depend on THOSE, not the entry objects. An upstream
  // selector/store that hands back a fresh object every render (seen for real: a test
  // mock rebuilding consentCatalog inline) would otherwise make this effect see a
  // "changed" dependency on every render forever, call setForm, re-render, repeat — a
  // genuine infinite render loop (confirmed 2026-10, see RegisterVisitForm.consentCatalogStability
  // test). Depending on primitives makes the effect correct regardless of whether the
  // objects above are ever memoized upstream.
  const consentGrantedResolved = resolveConsentGranted(digitalRecordsEntry, consentCatalogEntry);
  useEffect(() => {
    if (view !== 'visit') return;
    setForm((f) => (f.consentGranted === consentGrantedResolved ? f : { ...f, consentGranted: consentGrantedResolved }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, consentGrantedResolved]);

  const handleMobileChange = useCallback((value) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 10);
    setForm((f) => ({ ...f, mobile: cleaned, patient_id: '' }));
    setSelectedPatient(null);
    clearAbhaDup();
    setRelationHint(false);
    setAbhaOldUhid('');
    setAppointmentLinked(false);
    setExpiredDismissed(false);
    clearDetected();
    setView('mobile');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (cleaned.length === 10) {
      debounceRef.current = setTimeout(() => {
        searchPatient(cleaned);
        detectByMobile(cleaned); // Auto-detect appointment
      }, 500);
    } else {
      clearPatientLookup();
    }
  }, [searchPatient, clearPatientLookup, detectByMobile, clearDetected, clearAbhaDup]);

  // Came from the ABHA flow with the mobile already typed there: run the same lookup the mobile
  // step runs, so the visit goes straight to the matched patient or the prefilled new-patient form.
  const abhaMobileAppliedRef = useRef(false);
  useEffect(() => {
    if (abhaMobileAppliedRef.current) return;
    const typed = snapshotLookupMobile(initialAbhaSnapshot);
    if (!typed || form.mobile) return;
    abhaMobileAppliedRef.current = true;
    handleMobileChange(typed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAbhaSnapshot, form.mobile]);

  // ── Auto-link appointment when detected (within window only) ──
  useEffect(() => {
    if (detectedAppointment && !appointmentLinked && detectedAppointment.within_window) {
      setAppointmentLinked(true);

      // Split patient_name into first/last as fallback when first_name/last_name are missing
      // Smart split: leading initials (≤2 chars like "J P") stay with first name
      //   "J P"          → first: "J P",  last: ""
      //   "J P Chaudary" → first: "J P",  last: "Chaudary"
      //   "Rahul Kumar"  → first: "Rahul", last: "Kumar"
      let firstName = detectedAppointment.first_name || '';
      let lastName = detectedAppointment.last_name || '';
      if (!firstName && detectedAppointment.patient_name) {
        const parts = detectedAppointment.patient_name.trim().split(/\s+/);
        // Find the first "real" name token (length > 2) after any leading initials
        let splitIdx = 0;
        while (splitIdx < parts.length && parts[splitIdx].length <= 2) {
          splitIdx++;
        }
        if (splitIdx === 0) {
          // No leading initials — normal split: first word = first, rest = last
          firstName = parts[0] || '';
          lastName = parts.slice(1).join(' ') || '';
        } else if (splitIdx >= parts.length) {
          // ALL tokens are initials (e.g. "J P") — treat entire string as first name
          firstName = parts.join(' ');
          lastName = '';
        } else {
          // Leading initials + real name(s) — initials = first, rest = last
          firstName = parts.slice(0, splitIdx).join(' ');
          lastName = parts.slice(splitIdx).join(' ');
        }
      }

      // Auto-set visit type, doctor, and prefill patient details from appointment
      setForm((f) => ({
        ...f,
        visitType: 'APPOINTMENT',
        visitDoctorId: detectedAppointment.doctor_member_id || f.visitDoctorId,
        appointment_id: detectedAppointment.appointment_id,
        first_name: firstName || f.first_name,
        last_name: lastName || f.last_name,
        age: detectedAppointment.age || f.age,
        // appointment rows copy the patient's stored gender verbatim (may be legacy "M")
        gender: normalizeGender(detectedAppointment.gender) || f.gender,
        dob: detectedAppointment.dob || f.dob,
      }));

      // Skip to visit step if we have enough data for existing patient
      // (allows user to review/edit the prefilled details)
    }
  }, [detectedAppointment, appointmentLinked]);

  useEffect(() => {
    if (patientLookup === null) return;          // no search performed yet
    if (Array.isArray(patientLookup) && patientLookup.length > 0) {
      setView('select');
    } else if (form.mobile.length === 10) {
      // empty array → number not found → go straight to new-patient form
      setView('new_patient');
      setForm((f) => ({ ...f, relation: 'SELF' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientLookup]);

  // The server replaces the address (and DOB/gender) when it links an ABHA to an
  // EXISTING patient (OR-121/130/131), so the picked row is stale after every
  // successful link: re-read that ONE patient (one GET per completed link) and
  // re-seed the form from it. A failed read leaves the row as it was.
  // F7: a field staff edited while the GET was in flight keeps the edit; only fields
  // still equal to what the form held when the re-read started take the server value.
  const refreshLinkedPatient = async (patientId) => {
    const before = formRef.current;
    try {
      const fresh = await getPatient(patientId);
      if (!fresh) return;
      setSelectedPatient((p) => (p && p.patient_id === patientId ? { ...p, ...fresh, abha_status: 'VERIFIED' } : p));
      const incoming = patientFormFields(fresh);
      setForm((f) => {
        if (f.patient_id !== patientId) return f;
        const next = { ...f };
        Object.keys(incoming).forEach((k) => { if (f[k] === before[k]) next[k] = incoming[k]; });
        return next;
      });
    } catch (e) {
      /* keep the row we have */
    }
  };
  const [abhaVerifyPatient, setAbhaVerifyPatient] = useState(null);
  const handleSelectPatient = (patient) => {
    clearAbhaDup();
    setSelectedPatient(patient);
    setForm((f) => ({ ...f, ...patientFormFields(patient) }));
    setAbhaOldUhid('');
    setRelationHint(false);
    setScanNotUsedNote(false);
    setScanLinkNote('');
    if (abhaResult?.scanId) {
      // Scan & Share for an EXISTING patient (R67): drop the scan, tell staff up front and open the
      // normal OTP verify (prefilled mobile) -> same-person dialog -> link.
      // R78: an already-VERIFIED patient first tries the claim; the server attaches it without an OTP
      // when the scanned ABHA equals the verified one, else 409 SCAN_SHARE_EXISTING_PATIENT -> R67 path.
      const scanId = abhaResult.scanId;
      setAbhaResult(null);
      if (patient.abha_status === 'VERIFIED' && canScanShare) {
        claimDiscoveredScan({ patientId: patient.patient_id, scanId })
          .then((r) => {
            setScanLinkNote(r.sameAbha ? "Linked to this patient's existing ABHA." : 'ABHA linked from Scan & Share.');
            refreshLinkedPatient(patient.patient_id); // the claim may have filled an empty address (OR-137)
          })
          .catch((e) => {
            if (e?.abhaCode === 'SCAN_SHARE_EXISTING_PATIENT') setAbhaVerifyPatient(patient);
            else setScanLinkNote(SCAN_SHARE_COPY.claimFailed(claimErrorMessage(e)));
          });
      } else setAbhaVerifyPatient(patient);
    } else if (abhaResult && patient.abha_status !== 'VERIFIED') setLinkConfirmPatient(patient);
    setView('visit');
  };

  // "Open patient" on the already-linked notice: pick that existing patient instead of a new record.
  const handleOpenDuplicatePatient = () => abhaDup.open(handleSelectPatient);
  const hasSelfInLookup = () => Array.isArray(patientLookup) &&
    patientLookup.some((p) => (p.relation || 'SELF') === 'SELF');
  const handleAddFamilyMember = () => {
    // R121: a held scan belongs to whoever scanned, not to this family member. Drop it (it stays in the waiting list).
    if (abhaResult?.scanId) {
      setAbhaResult(null);
      setScanNotUsedNote(true);
    }
    setRelationHint(hasSelfInLookup());
    setAbhaOldUhid('');
    const hasSelf = hasSelfInLookup();
    setSelectedPatient(null);
    setForm((f) => ({
      ...f,
      patient_id: '',
      first_name: '',
      last_name: '',
      relation: hasSelf ? '' : 'SELF',
      age: '',
      dob: '',
      gender: '',
      aadhaar_no: '',
      birth_certificate: '',
      ...EMPTY_ADDRESS,
      allergies: '',
    }));
    setView('new_patient');
  };

  const handleAbhaLinked = () => {
    const linkedId = linkConfirmPatient?.patient_id;
    setSelectedPatient((p) => (p ? { ...p, abha_status: 'VERIFIED' } : p));
    setLinkConfirmPatient(null);
    if (linkedId) refreshLinkedPatient(linkedId);
  };
  // Session expired in the dialog: close it and re-open the ABHA verify step for the same patient
  // (the old ABHA is dropped; a fresh verify re-opens the same-person dialog).
  const handleAbhaVerifyAgain = () => {
    setAbhaVerifyPatient(linkConfirmPatient);
    setAbhaResult(null);
    setLinkConfirmPatient(null);
  };
  const handleAbhaVerifyComplete = (snapshot) => {
    if (!snapshot) return;
    setAbhaResult(snapshot);
    setLinkConfirmPatient(abhaVerifyPatient);
    setAbhaVerifyPatient(null);
  };
  const handleAbhaLinkCancel = () => {
    setAbhaResult(null);          // staff chose not to link — never link silently at submit
    setLinkConfirmPatient(null);
  };
  // Different person: new patient prefilled from the ABHA; the old record is left untouched.
  const handleAbhaRegisterNew = ({ oldUhid }) => {
    handleAddFamilyMember();
    const hasSelf = Array.isArray(patientLookup) &&
      patientLookup.some((p) => (p.relation || 'SELF') === 'SELF');
    setRelationHint(hasSelf);
    setForm((f) => ({ ...f, ...abhaFormPatch(abhaResult, f), ...(hasSelf ? { relation: '' } : {}) }));
    setAbhaOldUhid(oldUhid || '');
    setLinkConfirmPatient(null);
  };

  const handleConfirmNewPatient = () => {
    const errors = {};
    if (!form.first_name.trim()) errors.first_name = 'First name is required';
    if (!form.last_name.trim() && isLastNameRequired(abhaResult)) errors.last_name = 'Last name is required';
    if (!form.relation) errors.relation = 'Relation is required';
    if (form.address_pincode.trim() && !PINCODE_RE.test(form.address_pincode.trim())) errors.address_pincode = PINCODE_ERROR;
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setView('visit');
  };

  const validate = () => {
    const errors = {};
    if (!form.mobile || form.mobile.length !== 10) errors.mobile = 'Valid 10-digit mobile required';
    if (form.visitProviderType === 'NURSE') {
      // OTC XOR: exactly one attendee. The chosen kind decides which id is
      // required; one shared error keys off the attendee area (C2 mirrors the
      // server-authoritative 422). The kind-switch already cleared the other id.
      const attendeeId = form.otcAttendeeKind === 'DOCTOR' ? form.visitDoctorId : form.visitNurseId;
      if (!attendeeId) errors.otcAttendee = OTC_ATTENDEE_REQUIRED;
    } else if (form.visitProviderType === 'LAB') {
      if (!form.visitLabTechId) errors.visitLabTechId = 'Select a lab technician';
    } else if (!form.visitDoctorId) {
      errors.visitDoctorId = 'Select a doctor';
    }
    if (!form.patient_id) {
      if (!form.first_name.trim()) errors.first_name = 'First name is required';
      if (!form.last_name.trim() && isLastNameRequired(abhaResult)) errors.last_name = 'Last name is required';
    }
    if (!form.paymentMode) errors.paymentMode = 'Select a payment mode';
    // A2 — Department is mandatory when the org has departments (the visit's
    // dept-of-care is stamped at registration). Emergency visits and orgs with
    // no departments are exempt (deptResolution.required is false for both).
    if (deptResolution.required && !form.department_id) {
      errors.department_id = 'Select a department';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Procedures-on-New-Visits — selected procedure lines + their folded fee.
  // Stable callback (defined before any early return so hook order is fixed).
  const handleProceduresChange = useCallback((lines, total) => {
    setProcedureLines(lines || []);
    setProcedureFeeTotal(total || 0);
  }, []);

  // Lab Visit — selected investigations (lab tests) to order on this visit.
  const handleInvestigationsChange = useCallback((lines) => {
    setInvestigationLines(lines || []);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();
    setBlockError('');
    setRegistrationAmountPaid(null);
    setRegistrationLineItems(null);
    setRegistrationReceiptNumber(null);
    setRegistrationDepartmentName(null);
    if (!validate()) return;
    // Doctor-collect policy: in clinics where doctors may not collect payment,
    // a doctor must not register at all (collection is Reception's job). Block
    // BEFORE creating the visit, regardless of whether procedures are attached.
    // The backend also rejects doctor collection (defence-in-depth).
    if ((role || '').toUpperCase() === STAFF_ROLES.DOCTOR) {
      await fetchProcedurePaymentSettings();
      if (!useOrgSettingsStore.getState().doctorCanCollectPayment) {
        setBlockError(
          'Registration is disabled. Please send the patient to Reception for registration.'
        );
        return;
      }
    }
    try {
      // Capture who advised the work (for the printed token) from the selected
      // lines: DOCTOR + name when doctor-advised, else SELF. Stored on the visit
      // so the registration-time and reprinted tokens agree.
      // R1-8 — the explicit "Advised by doctor" (picked hospital doctor, or the
      // typed outside name) wins; empty → today's automatic value. Resolved here,
      // once, and stored on the visit.
      const pickedDoctor = doctors.find((d) => d.member_id === form.advisedByDoctorId);
      const attribution = resolveAdvisedBy(form.visitProviderType, {
        explicitName: form.advisedByTypedName ?? pickedDoctor?.name,
        procedureLines,
        investigationLines,
      });
      // F13 — from here the SuccessPopup can mount (registerVisit sets
      // createdVisit); mark the token money as still being prepared so its
      // Print button stays gated until the collect responses below produce it.
      // R22 one-ABHA-one-patient: a NEW patient carrying a verified ABHA must not be created if that
      // ABHA already belongs to a patient — show the notice and create nothing.
      const newPatientAbha = !selectedPatient?.patient_id && !form.patient_id
        ? resolvePatientIdentity(abhaResult)?.abhaNumber : null;
      if (newPatientAbha && !isMaskedAbhaNumber(newPatientAbha) && await abhaDup.check(newPatientAbha)) return;
      clearAbhaDup();
      setTokenPreparing(true);
      const data = await registerVisit({
        ...form,
        age: effectiveAge(form),
        ...(form.patient_id ? NO_ADDRESS_KEYS : null),
        ...attribution,
        // R88 — server-side duplicate guard (new patient only; visitApi drops it for an existing one).
        abhaNumber: newPatientAbha && !isMaskedAbhaNumber(newPatientAbha) ? newPatientAbha : undefined,
        scanId: !selectedPatient?.patient_id && !form.patient_id ? abhaResult?.scanId || undefined : undefined,
      });
      const visitId = data?.visit?.visit_id || data?.visit?.id;
      const registeredPatientId = data?.patient?.patient_id || form.patient_id;
      // SS-5: resolve the ABHA identity — abhaResult only now (the in-form live-WebSocket Scan &
      // Share widget that used to supply a second, scan-sourced candidate here has been removed;
      // the desk-wide Scan & Share panel's claim by scan_id further below replaced the per-mobile lookup).
      // An existing patient already ABHA-verified needs no ABHA work at all — skip the link.
      const alreadyVerified = selectedPatient?.abha_status === 'VERIFIED';
      // An EXISTING patient is only ever linked through the "same person?" dialog, never here.
      const linkingExisting = Boolean(selectedPatient?.patient_id || form.patient_id);
      const identity = alreadyVerified || linkingExisting ? null : resolvePatientIdentity(abhaResult);
      // Skip masked identifiers (e.g. a returning patient's record snapshot carries a masked
      // number) — never re-link a display-only value over the canonical one.
      let abhaLinkNote = '';
      let abhaLinked = false;   // a link/claim succeeded => the server replaced the address with ABHA's
      if (identity?.abhaNumber && !isMaskedAbhaNumber(identity.abhaNumber) && registeredPatientId) {
        try {
          await linkABHAToPatient({
            patientId: registeredPatientId,
            abhaNumber: identity.abhaNumber,
            abhaAddress: identity.abhaAddress || '',
            recordVersion: 0,
            profile: identity.profile,   // full profile → ABDM snapshot + demographics enrichment
          });
          abhaLinked = true;
        } catch (e) {
          if ((e?.linkCode || e?.abhaCode) === ABHA_ALREADY_LINKED_CODE) abhaLinkNote = ABHA_ALREADY_LINKED_NOTE;
          else if (e?.linkCode === 'ABHA_DOB_UNAVAILABLE') abhaLinkNote = 'ABHA could not be linked because the ABHA has only a birth year.';
        }
      }
      // R56 — a scan picked from the desk-wide Scan & Share panel arrives as initialAbhaSnapshot.scanId.
      // Claim it for the registered (or selected) patient; a failure is shown, never swallowed.
      if (canScanShare && abhaResult?.scanId && registeredPatientId && !linkingExisting) {
        try {
          await claimDiscoveredScan({ patientId: registeredPatientId, scanId: abhaResult.scanId });
          abhaLinked = true;
          // No re-read here: the form resets after register, so nothing would consume it (QA-ADDR-5).
        } catch (e) {
          abhaLinkNote = SCAN_SHARE_COPY.claimFailed(claimErrorMessage(e));
        }
      }
      // D4: an OTP link above replaced the typed address with ABHA's. When staff edited any
      // ABHA-filled field before saving, send all six back ONCE (the server then marks the
      // differing ones Edited). No edits (or no ABHA address) => no extra request.
      // OR-137: a Scan & Share claim never replaces a saved address => never this PATCH.
      let addressNote = '';
      if (abhaLinked && !linkingExisting && registeredPatientId && needsPostLinkAddressPatch(form, abhaResult)) {
        try {
          await updatePatientDemographics(registeredPatientId, addressOf(form));
        } catch (e) {
          addressNote = "The address changes couldn't be saved.";
        }
      }
      // F2 — resolve the submitted department_id to its display name from the
      // single-source dept list (departmentStore) BEFORE the end-of-submit
      // `setForm(INITIAL_FORM)` clears form.department_id. Emergency/dept-less
      // visits have no department_id → stays null → token omits the row.
      if (form.department_id) {
        const dept = departments.find((d) => (d.dept_id || d.id) === form.department_id);
        setRegistrationDepartmentName(dept?.name || null);
      }
      // Single-source capability gate — what THIS visit type may bill. Mirrors
      // the render gates + the provider-switch clear, so a stale selection from
      // a prior provider type can never be submitted (the procedure-on-lab-visit
      // blunder). Backend enforces the same invariant as the durable guard.
      const caps = visitTypeCapabilities(form.visitProviderType);
      // Walk-in Lab Visit — realise each chosen investigation onto the visit,
      // assigned to the attending technician. Two sources (see
      // RegistrationInvestigationsSection): a doctor-ADVISED order already
      // exists → ASSIGN it (no duplicate, no re-charge if already paid); a
      // fresh catalog pick → ADVISE a new order. Both then collect together.
      // Shared across the lab AND procedure legs below — a failure in either
      // (or both) collapses into ONE "Visit registered, but: …" banner so
      // staff never see a false "clean" success when money was lost.
      const warnings = [abhaLinkNote, addressNote].filter(Boolean);
      // F13 — accumulate the money the client ACTUALLY collected in this submit
      // so the token derives its Amount Paid / line items from held data (the
      // collect responses), never from a racy receipts read-back.
      let bundledRes = null;   // collectBundledPayment response ({ total, errors })
      let labCollected = 0;    // rupees the lab bundle collected (form-known sum)
      let labOrders = null;    // updated orders the lab bundle returned (carry the minted receipt_number)
      let billingOnly = 0;     // consult/registration written w/o a bundle
      // F16 — duplicate-advice cancels are NOT money-bearing (one result serves
      // every doctor who advised the same test), so they must not sit on the
      // Print-button gate. Collected here for successfully-assigned lines and
      // fired in the BACKGROUND after the gate clears (below); a failed cancel
      // still appends to the same "Visit registered, but: …" warnings banner.
      const dupCancels = [];   // { dupId, name } to cancel off the critical path
      if (caps.lab && visitId && investigationLines.length > 0) {
        const labPatientId = data?.patient?.patient_id || data?.visit?.patient_id || form.patient_id;
        const toCollect = [];  // lab_order_ids to bill as ONE shared receipt

        // 1. Existing doctor-advised orders → assign the SAME order to the tech.
        // A test advised more than once is one grouped row: assign its primary
        // order and cancel the duplicate advisements (the patient does it once).
        const advisedLines = investigationLines.filter((l) => l.source === 'advised' && l.lab_order_id);
        // F16 — the assigns are INDEPENDENT orders (each re-points a distinct
        // advised order onto this lab visit), so fire them in PARALLEL instead
        // of one serial round-trip per line. They still all resolve BEFORE the
        // collect below (which pays them) — Promise.all preserves that ordering.
        const assignResults = await Promise.all(
          advisedLines.map((l) => assignLabOrder(l.lab_order_id, {
            new_assignee_member_id: form.visitLabTechId,
            new_assignee_role: STAFF_ROLES.LAB_TECHNICIAN,
            // Carry the doctor-advised order off the OPD visit onto THIS lab
            // visit, so its receipt/payment land on the lab visit (backend
            // re-points visit_id + keeps origin_visit_id for history).
            visit_id: visitId,
          })),
        );
        assignResults.forEach((res, i) => {
          const l = advisedLines[i];
          if (!res.success) {
            warnings.push(`Couldn't assign "${l.name}" to the technician.`);
            return;
          }
          // Cancel the duplicate advisements of the same test (one result serves
          // all the doctors who advised it) — queued for the background fire
          // AFTER the gate clears, since a cancel produces no token money.
          for (const dupId of l.cancel_lab_order_ids || []) {
            dupCancels.push({ dupId, name: l.name });
          }
          // Only the not-yet-paid, chargeable ones get collected now.
          if (!l.paid && !l.skip_payment && Number(l.cost) > 0) toCollect.push(l.lab_order_id);
        });

        // 2. Fresh catalog investigations → advise a new order assigned to the tech.
        const newLines = investigationLines.filter((l) => l.source !== 'advised');
        const adviseResults = await Promise.all(
          newLines.map((inv) =>
            adviseLabOrder({
              visit_id: visitId,
              patient_id: labPatientId,
              catalog_procedure_id: inv.procedure_id,
              assigned_to_member_id: form.visitLabTechId,
              assigned_to_role: STAFF_ROLES.LAB_TECHNICIAN,
              patient_name: patientName || undefined, // blank → backend fills from the patient record
              queue_number: data?.queue_number,
              // W6/C5 — registration NEW-catalog line only; advised/assign
              // legs never send quantity (stay qty=1, server-side).
              quantity: inv.quantity || 1,
            }),
          ),
        );
        const failed = adviseResults.filter((r) => !r.success);
        if (failed.length) {
          warnings.push(`${failed.length} of ${newLines.length} investigation(s) couldn't be added to the lab queue.`);
        }
        adviseResults.forEach((r, i) => {
          const inv = newLines[i];
          if (r.success && r.data?.lab_order_id && inv && !inv.skip_payment && Number(inv.cost) > 0) {
            toCollect.push(r.data.lab_order_id);
          }
        });

        // Bill the chargeable, unpaid tests as ONE shared receipt — the app's
        // "collected together = one receipt" rule. A failure is surfaced (not
        // silently swallowed) so it can be collected from history.
        if (toCollect.length) {
          const mode = (form.paymentMode || 'CASH').toUpperCase();
          const pay = await collectLabPaymentBundle(toCollect, mode);
          if (!pay.success) {
            warnings.push("Payment couldn't be collected for the lab test(s) — collect it from the patient's lab history.");
          } else {
            // F13 — the token's Amount Paid for the lab leg comes from the
            // chargeable lines the client just billed (qty × unit), not a
            // read-back. Matches the receipts sum (asserted, RC5).
            labCollected = investigationLines.reduce((s, l) => (
              (!l.paid && !l.skip_payment && Number(l.cost) > 0)
                ? s + Number(l.cost) * (Number(l.quantity) || 1)
                : s
            ), 0);
            // F16/W7 — the updated orders carry the shared minted receipt
            // number; hold them so a lab-only visit's token prints its Rx No.
            labOrders = pay.data || null;
          }
        }
      }
      if (caps.procedures && visitId && procedureLines.length > 0) {
        // Procedures-on-New-Visits — one bundled call that folds the
        // consultation + registration fees together with the selected
        // procedures (pay-and-move / move-only / create-new) onto the new
        // visit, producing one receipt. Gated on caps.procedures so a stale
        // OPD selection can never bill onto a Lab Visit. The bundled call can
        // report a partial failure WITHOUT throwing (HTTP 200 + populated
        // `errors[]` — the shared per-leg swallow architecture in the
        // backend); a thrown call (network/500) is a failure too. Either way
        // this must surface, never a silent "clean" success (peer pattern:
        // ReceptionistTaskWidget.jsx reads `res.errors.length` the same way).
        try {
          const res = await collectBundledPayment(visitId, {
            consultation_fee: consultationFee !== '' ? Number(consultationFee) : undefined,
            registration_fee: registrationFee !== '' ? Number(registrationFee) : undefined,
            payment_mode: form.paymentMode || 'CASH',
            sitting_moves: procedureLines,
          });
          bundledRes = res; // F13 — server-authoritative { total, errors } for the token
          if (res?.errors?.length) {
            warnings.push(
              `${res.errors.length} of ${procedureLines.length} procedure(s) couldn't be collected — collect from the patient's procedures list.`,
            );
          }
          // W1-T5-FE2 — backend T5-BE (frozen additive contract): a bundled
          // `create_new` leg whose requested assignee turned out ineligible is
          // created UNASSIGNED (payment still succeeds) and the SUCCESS
          // component carries `assignee_downgraded: true`. This is an INFO
          // notice, not an error — same `warnings` banner, never blocks and
          // never touches the success popup. `downgrade_reason` isn't branched
          // on (only known value today is "assignee_ineligible") — an unknown
          // future reason still renders this same generic notice.
          const downgraded = (res?.components || []).filter((c) => c?.assignee_downgraded);
          if (downgraded.length) {
            warnings.push(
              `${downgraded.length} procedure(s) will be picked up from the procedures queue — the selected staff member isn't permitted to perform them.`,
            );
          }
        } catch (_) {
          warnings.push("Payment couldn't be collected for the procedure(s) — collect from the patient's procedures list.");
        }
      } else if (visitId && (consultationFee || registrationFee)) {
        try {
          await updateVisitBilling(visitId, { consultationFee, registrationFee });
          // F13 — a fee-only visit (no procedures to bundle): the token's
          // Amount Paid is the fees the client just wrote, not a read-back.
          billingOnly = Number(consultationFee || 0) + Number(registrationFee || 0);
        } catch (_) {}
      }
      if (warnings.length) {
        setBlockError(`Visit registered, but: ${warnings.join(' ')}`);
      }
      // F13 (2026-08-02) — RACE-FREE token money. Derive the printed Amount Paid
      // + line items from data the client ALREADY HOLDS now that every collect
      // call above has resolved (the bundled-collect RESPONSE `total`/`errors`,
      // the lab bundle sum, the fee-only write, or the register response's own
      // fees) — NEVER from a receipts read-back that can race the write or fail.
      // This flips `tokenMoneyReady` true, un-gating the Print button with the
      // CORRECT amount even on a fast click / slow network / failed read-back.
      if (visitId) {
        const money = tokenMoneyFromCollect({
          bundled: bundledRes,
          labCollected,
          billingOnly,
          visit: data?.visit,
          consultationFee: consultationFee !== '' ? Number(consultationFee) : 0,
          registrationFee: registrationFee !== '' ? Number(registrationFee) : 0,
          procedureLines,
          investigationLines,
        });
        setRegistrationAmountPaid(money.amountPaid);
        setRegistrationLineItems(money.lineItems);
        // F16/W7 — the register response's `visit.receipt_number` is NULL now
        // (numbers mint at PAYMENT), so fold the minted number out of the
        // collect responses the client holds; null for an unpaid registration.
        setRegistrationReceiptNumber(
          tokenReceiptNumberFromCollect({ bundled: bundledRes, labOrders }),
        );
        // GST-DOCNO/A2-d — the printed document-number string for the SAME
        // collect response, so the token's "Receipt No." row reads the
        // stored string when the backend has stamped it.
        setRegistrationReceiptNumberPrinted(
          tokenReceiptNumberPrintedFromCollect({ bundled: bundledRes, labOrders }),
        );
        // F16 — this submit now owns the token's money state. Marks the visit
        // whose DETACHED reconcile below is allowed to upgrade it (guards a late
        // arrival from leaking into the next registration / a closed popup).
        activeTokenVisitRef.current = visitId;
        setTokenPreparing(false); // un-gate — the reconcile below never blocks it

        // F16 — DUPLICATE-CANCEL: fire off the critical path (post-gate,
        // background). Non-money housekeeping; a failure still appends to the
        // same "Visit registered, but: …" banner so it isn't silently dropped.
        if (dupCancels.length) {
          Promise.all(
            dupCancels.map(({ dupId, name }) =>
              cancelLabOrder(dupId, { reason: 'Duplicate advice — consolidated at registration' })
                .then((c) => (c && c.success ? null : name))
                .catch(() => name)),
          ).then((names) => {
            const failed = names.filter(Boolean);
            if (failed.length) {
              // Only warn about a duplicate whose PRIMARY still shows on THIS
              // submit's token (guard a late resolve after the next reg starts).
              if (activeTokenVisitRef.current !== visitId) return;
              setBlockError((prev) => {
                const msg = `Couldn't remove ${failed.length} duplicate advisement(s).`;
                return prev ? `${prev} ${msg}` : `Visit registered, but: ${msg}`;
              });
            }
          });
        }

        // RECONCILE / UPGRADE (not a gate): once the receipts read resolves it
        // is the canonical itemized source (real names/qty/source_type). Adopt
        // it ONLY when it has CAUGHT UP — its sum is >= the response-derived
        // amount. A stale / eventually-consistent read-back that returns empty
        // or undercounts (the original F1/F13 race) must NEVER downgrade the
        // authoritative collect-response total; on failure OR staleness we keep
        // the response-derived money, so the token can never print less than
        // what the server said was collected.
        //
        // F16 — DETACHED: fire-and-forget so the form reset + view switch below
        // run the instant the collect-derived money lands, never waiting on the
        // slow strongly-consistent `GET /receipts` read. A LATE arrival (popup
        // closed, or a NEXT registration already started) is a no-op: it only
        // upgrades state still owned by its own submit (`activeTokenVisitRef`),
        // and only UPWARD (caught-up) — so a reprint from the popup can never
        // show a downgraded / stale-visit amount.
        listReceiptsForVisit(visitId)
          .then((rec) => {
            if (activeTokenVisitRef.current !== visitId) return;
            if (rec.success && sumCollectedRupees(rec.data) >= money.amountPaid) {
              const rows = rec.data;
              setRegistrationAmountPaid(sumCollectedRupees(rows));
              setRegistrationLineItems(shouldItemizeToken(rows) ? tokenLineItemsFromReceipts(rows) : null);
            }
          })
          .catch(() => { /* keep the response-derived money — never print empty */ });
      }
      setConsultationFee('');
      setRegistrationFee('');
      setProcedureLines([]);
      setProcedureFeeTotal(0);
      setInvestigationLines([]);
      setFeeWaiverNote('');
      setForm(INITIAL_FORM);
      clearPatientLookup();
      setSelectedPatient(null);
      setView('mobile');
      setValidationErrors({});
      setAppointmentLinked(false);
      setExpiredDismissed(false);
    } catch (err) {
      // F13 — a failed register/collect never leaves the Print button stranded
      // in the "preparing" state (a popup, if any, must stay actionable).
      setTokenPreparing(false);
      // S7/S8 — surface a provider↔department mismatch (422) DISTINCTLY from a
      // missing dept (400): a 422 means the chosen dept isn't valid for the
      // selected ACTIVE PROVIDER (doctor/nurse/lab tech; or is
      // unknown/foreign/inactive) → prompt a RE-PICK by flagging the Department
      // field. The generic store `error` banner still shows too. No visit was
      // created on either (validate() ran client-side; server rejected before the
      // row) so state is left intact for the correction.
      const { kind, message } = classifyRegisterError(err);
      if (await abhaDup.fromError(err)) {
        // R88: server refused a second patient for an already-linked ABHA — notice replaces the banner.
        clearError();
        return;
      }
      if (isSelfTakenError(err)) {
        // Backend allows one SELF per mobile: send staff back to pick the Relation, inline (no banner).
        clearError();
        setForm((f) => ({ ...f, relation: '' }));
        setRelationHint(true);
        setView('new_patient');
        return;
      }
      if (err?.code === ADMISSION_ACCESS_ERROR_CODE.PROVIDER_OUT_OF_SCOPE) {
        // 403 — the attending provider left the viewer's departments (stale
        // list). Replace the generic banner with the ratified sentence for the
        // role being attended (doctor / nurse / lab technician).
        setError(PROVIDER_OUT_OF_SCOPE_COPY_BY_ROLE[
          wantsLabTechs ? STAFF_ROLES.LAB_TECHNICIAN : wantsNurses ? STAFF_ROLES.NURSE : STAFF_ROLES.DOCTOR
        ]);
      } else if (kind === 'DEPT_MISMATCH') {
        setValidationErrors((prev) => ({
          ...prev,
          department_id: message || 'Re-pick the department for the selected provider.',
        }));
      } else if (kind === 'DEPT_REQUIRED') {
        setValidationErrors((prev) => ({
          ...prev,
          department_id: message || 'Department is required.',
        }));
      }
    }
  };

  // After queue popup closes → scoped roles (Doctor) go back to dashboard;
  // unscoped roles (Admin/Receptionist) stay on register page for the next visit.
  const handleDismissPopup = useCallback(() => {
    // F16 — the token is gone; a DETACHED reconcile still in flight for it must
    // become a no-op (never setState for a closed popup / leak into the next
    // registration). Its guard compares against this ref.
    activeTokenVisitRef.current = null;
    clearCreatedVisit();
    if (isScoped && onSuccess) return onSuccess();
    setConsultationFee('');
    setRegistrationFee('');
    setForm(INITIAL_FORM);
    clearPatientLookup();
    setSelectedPatient(null);
    clearAbhaDup();
    setView('mobile');
    setValidationErrors({});
    setAppointmentLinked(false);
    setExpiredDismissed(false);
    clearDetected();
    setAbhaResult(null);
    setAbhaOldUhid('');
    setRelationHint(false);
    setScanNotUsedNote(false);
    setLinkConfirmPatient(null);
  }, [clearCreatedVisit, clearPatientLookup, isScoped, onSuccess, clearDetected, clearAbhaDup]);

  // ── Helper: full reset back to mobile step ──
  const resetToMobile = useCallback(() => {
    clearAbhaDup();
    setForm(INITIAL_FORM);
    clearPatientLookup();
    setSelectedPatient(null);
    setView('mobile');
    setValidationErrors({});
    setAppointmentLinked(false);
    setExpiredDismissed(false);
    clearDetected();
    setAbhaResult(null);
    setAbhaOldUhid('');
    setRelationHint(false);
    setScanNotUsedNote(false);
    setLinkConfirmPatient(null);
  }, [clearPatientLookup, clearDetected, clearAbhaDup]);

  // After an ABHA verify/create, the fields ABHA populated are authoritative — lock any that came
  // back non-blank; leave the ones ABHA didn't provide editable so staff can fill the gaps.
  const abhaLocked = (val) => Boolean(abhaResult) && Boolean(String(val ?? '').trim());
  const lockedInputStyle = (locked) => (locked
    ? { opacity: 0.75, cursor: 'not-allowed', background: isDark ? 'rgba(255,255,255,0.03)' : '#f1f5f9' }
    : {});
  // An existing patient who is ABHA-verified has authoritative demographics — block editing them.
  const abhaVerifiedExisting = Boolean(selectedPatient)
    && (Boolean(abhaResult) || selectedPatient?.abha_status === 'VERIFIED');
  // OR-127: a usable DOB makes the age read-only and derived (same rule the submit sends).
  const dobKnown = Boolean(ageFromDob(form.dob));
  // The old one-line address text, only while the patient has no structured address (OR-129 hint).
  const legacyAddress = selectedPatient ? pickAddress(selectedPatient).legacy : null;

  const handleBack = () => {
    clearAbhaDup(); // also cancels a pending Open patient
    if (view === 'visit') {
      if (selectedPatient) {
        // Go back to patient selection
        setView('select');
        setSelectedPatient(null);
        setForm((f) => ({ ...f, patient_id: '' }));
      } else if (Array.isArray(patientLookup) && patientLookup.length > 0) {
        setView('select');
      } else {
        resetToMobile();
      }
    } else if (view === 'new_patient') {
      if (Array.isArray(patientLookup) && patientLookup.length > 0) {
        setView('select');
      } else {
        resetToMobile();
      }
    } else if (view === 'edit_patient') {
      setView('visit');
    } else if (view === 'select') {
      resetToMobile();
    }
  };

  // ── Cancel: clear everything then call parent ──
  const handleCancel = () => {
    resetToMobile();
    if (onCancel) onCancel();
  };

  if (!canCreate) return null;

  // ── Edit patient handler: opens the edit view ──
  const handleEditPatient = () => {
    setValidationErrors({});
    setView('edit_patient');
  };

  // ── Confirm edit: save changes + persist for existing patients ──
  // ABHA-verified existing patient (Q1/OR-136): name, relation, DOB, gender, age and
  // Aadhaar stay locked — only address + allergies are written, and the locked
  // identity fields are neither validated nor sent.
  const handleConfirmEdit = async () => {
    const errors = {};
    if (!abhaVerifiedExisting) {
      if (!form.first_name.trim()) errors.first_name = 'First name is required';
      if (!form.last_name.trim()) errors.last_name = 'Last name is required';
      if (!form.relation) errors.relation = 'Relation is required';
    }
    if (form.address_pincode.trim() && !PINCODE_RE.test(form.address_pincode.trim())) errors.address_pincode = PINCODE_ERROR;
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    // For existing patients, persist changes to backend
    if (selectedPatient && form.patient_id) {
      setEditSaving(true);
      // The six go out TOGETHER and only when staff changed one (key absent = untouched
      // server-side, so an unrelated edit never rewrites — or converts — a legacy address).
      const seeded = addressOf(pickAddress(selectedPatient));
      const addressEdited = ADDRESS_FIELDS.some((f) => form[f].trim() !== seeded[f]);
      const sentAge = effectiveAge(form);
      try {
        const updated = await updatePatientDemographics(form.patient_id, {
          ...(abhaVerifiedExisting ? null : {
            firstName: form.first_name,
            lastName: form.last_name,
            age: sentAge || '',
            // only when changed: '' now reaches the server as a clear (QA-ADDR-3)
            ...(form.dob !== (selectedPatient.dob || '') ? { dob: form.dob } : null),
            gender: form.gender || '',
            relation: form.relation || '',
            aadhaarNo: form.aadhaar_no || '',
          }),
          allergies: form.allergies.trim() || null,
          ...(addressEdited ? addressOf(form) : null),
        });
        // Also update selectedPatient in-memory so summary reflects edits
        setSelectedPatient((prev) => ({
          ...prev,
          ...(abhaVerifiedExisting ? null : {
            first_name: form.first_name,
            last_name: form.last_name,
            relation: form.relation,
            age: sentAge,
            dob: form.dob,
            gender: form.gender,
            aadhaar_no: form.aadhaar_no,
          }),
          allergies: form.allergies,
          // The server dropped the legacy line when it wrote the six and re-marked
          // the changed fields Edited; mirror its answer so the marks stay true.
          ...(addressEdited ? { ...addressOf(form), address: null, address_source: updated?.address_source ?? prev?.address_source } : null),
        }));
      } catch (err) {
        console.error('Failed to update patient:', err);
        // Continue anyway — form state is already updated
      } finally {
        setEditSaving(false);
      }
    }
    setView('visit');
  };

  const patients = Array.isArray(patientLookup) ? patientLookup : [];
  const hasSelf = patients.some((p) => (p.relation || 'SELF') === 'SELF');
  const patientName = selectedPatient
    ? formatPatientName(selectedPatient)
    : formatPatientName(form);

  // ── Fee visibility ──
  // OTC / Lab Visit are doctor-less — no consultation fee (registration +
  // procedure fees still apply). showConsultFee already needs a doctor, so it's
  // naturally false for both, but keep the guard explicit.
  const isNurseVisit = form.visitProviderType === 'NURSE';
  const isLabVisit = form.visitProviderType === 'LAB';
  // Single-source capability gate for the render section (same rule the submit
  // guards + provider-switch clear use).
  const caps = visitTypeCapabilities(form.visitProviderType);
  const showConsultFee = !isNurseVisit && !isLabVisit && !!(orgFeeSettings && form.visitDoctorId && orgFeeSettings.doctor_fees?.[form.visitDoctorId] != null);
  const showRegFee = !!(orgFeeSettings?.registration_fee_enabled && !form.patient_id);
  // Lab Visit — chargeable investigations feed the fee panel + total so the
  // front desk sees what to collect. Skip-payment AND already-paid (doctor-
  // advised, prepaid) tests are excluded — they're carried forward, not re-charged.
  // W6/C5 — a line's amount is unit cost × quantity; `quantity` is only ever
  // set (>1 possible) on a `source:'new'` registration-picker line — advised
  // lines have no `quantity` field and read as 1. DISPLAY-ONLY: the server
  // recomputes final_cost from its own catalog at advise time.
  const investigationFeeTotal = investigationLines.reduce(
    (sum, inv) => sum + ((inv.skip_payment || inv.paid) ? 0 : (Number(inv.cost) || 0) * (Number(inv.quantity) || 1)), 0,
  );
  const showFeeSection = showConsultFee || showRegFee || procedureFeeTotal > 0 || investigationFeeTotal > 0;
  const feeTotal = (parseFloat(consultationFee) || 0) + (parseFloat(registrationFee) || 0)
    + (procedureFeeTotal || 0) + investigationFeeTotal;

  /* ═══ Styles — fill parent width, no narrow maxWidth ═══ */
  const card = {
    width: '100%',
    borderRadius: '1rem',
    overflow: 'hidden',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
    background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.85)',
    boxShadow: isDark
      ? '0 4px 24px rgba(0,0,0,0.3)'
      : '0 4px 24px rgba(0,0,0,0.06)',
  };

  const inputBase = (hasError) => ({
    width: '100%',
    padding: '0.75rem 0.875rem',
    borderRadius: '0.75rem',
    border: `1.5px solid ${hasError ? '#ef4444' : isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
    background: isDark ? 'rgba(255,255,255,0.06)' : '#f9fafb',
    color: isDark ? '#f5f5f7' : '#111827',
    fontSize: '0.9375rem',
    outline: 'none',
    transition: 'border 0.2s, box-shadow 0.2s',
    boxSizing: 'border-box',
  });

  const labelStyle = {
    display: 'block',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: isDark ? 'rgba(255,255,255,0.5)' : '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: '0.5rem',
  };

  const errorTextStyle = { margin: '0.375rem 0 0', fontSize: '0.75rem', color: '#ef4444' };
  const ageNoteStyle = { margin: '0.375rem 0 0', fontSize: '0.75rem', color: isDark ? '#9ca3af' : '#6b7280' };
  const mutedNoteStyle = { margin: '0.375rem 0 0', fontSize: '0.8125rem', color: isDark ? '#6b7280' : '#9ca3af' };

  // Status lines under a DOCTOR picker (the OPD select and the OTC-doctor select):
  // loading (never the org roster) / load failure / empty server list / the
  // department-first narrowing found nothing / footer for a dept-scoped viewer.
  // Plain closure, not a hook — safe to call from JSX.
  const doctorPickerNotes = () => (
    <>
      {doctorsLoading && <p style={mutedNoteStyle}>{DOCTORS_LOADING_COPY}</p>}
      {doctorsError && <p style={errorTextStyle}>{DOCTORS_LOAD_ERROR_COPY}</p>}
      {!doctorsLoading && !doctorsError && viewerDoctors.length === 0 && (
        <p style={mutedNoteStyle}>{doctorsEmptyCopy(doctorPickerIsWalled)}</p>
      )}
      {form.department_id && viewerDoctors.length > 0 && filteredDoctors.length === 0 && (
        <p style={mutedNoteStyle}>
          No doctors in the selected department. Change the department to see more.
        </p>
      )}
      {doctorPickerIsWalled && <p style={mutedNoteStyle}>{DOCTORS_SCOPED_FOOTER_COPY}</p>}
    </>
  );

  const btnPrimary = (disabled) => ({
    width: '100%',
    padding: '0.875rem',
    borderRadius: '0.75rem',
    border: 'none',
    background: 'linear-gradient(135deg, #0d9488, #14b8a6)',
    color: '#fff',
    fontWeight: 600,
    fontSize: '0.9375rem',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    transition: 'background 0.2s, opacity 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    boxShadow: '0 4px 14px rgba(13, 148, 136, 0.3)',
  });

  const btnOutline = {
    width: '100%',
    padding: '0.75rem',
    borderRadius: '0.75rem',
    border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'}`,
    background: 'transparent',
    color: isDark ? '#e8e9ed' : '#374151',
    fontWeight: 500,
    fontSize: '0.875rem',
    cursor: 'pointer',
    transition: 'background 0.2s, border-color 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
  };

  return (
    <>
    {linkConfirmPatient && abhaResult && (
      <AbhaLinkConfirmDialog
        patientId={linkConfirmPatient.patient_id}
        snapshot={abhaResult}
        onLinked={handleAbhaLinked}
        onRegisterNew={handleAbhaRegisterNew}
        onCancel={handleAbhaLinkCancel}
        onVerifyAgain={handleAbhaVerifyAgain}
      />
    )}
    {abhaVerifyPatient && (
      <div role="dialog" aria-modal="true" aria-label="Verify ABHA" style={{
        position: 'fixed', inset: 0, zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.5)', padding: '1rem', overflowY: 'auto',
      }}>
        <div style={{
          width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto', padding: '1rem 1.1rem 1.25rem',
          background: isDark ? '#1e293b' : '#ffffff', color: isDark ? '#f5f5f7' : '#111827',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#cbd5e1'}`, borderRadius: '0.9rem',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <span style={{ fontWeight: 700 }}>Verify ABHA</span>
            <button type="button" onClick={() => setAbhaVerifyPatient(null)} style={{
              padding: '0.375rem 0.75rem', borderRadius: '0.5rem', cursor: 'pointer', font: 'inherit',
              background: isDark ? '#374151' : '#f3f4f6', color: isDark ? '#f5f5f7' : '#374151',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#94a3b8'}`,
            }}>Close</button>
          </div>
          {initialAbhaSnapshot?.scanId && (
            <p data-testid="abha-verify-notice" style={{ margin: '0 0 0.75rem', fontSize: '0.8125rem' }}>{EXISTING_PATIENT_VERIFY_NOTICE}</p>
          )}
          <ABHASection
            patientId={abhaVerifyPatient.patient_id}
            abhaStatus={abhaVerifyPatient.abha_status || 'NOT_LINKED'}
            initialMode="verify"
            defaultOpen
            hideHeader
            patientName={formatPatientName(abhaVerifyPatient)}
            defaultMobile={abhaVerifyPatient.mobile || form.mobile}
            abhaResult={null}
            onAbhaComplete={handleAbhaVerifyComplete}
          />
        </div>
      </div>
    )}
    {createdVisit && (
      <SuccessPopup data={createdVisit} isDark={isDark} onClose={handleDismissPopup} isScoped={isScoped} branding={branding} amountPaid={registrationAmountPaid} lineItems={registrationLineItems} receiptNumber={registrationReceiptNumber} receiptNumberPrinted={registrationReceiptNumberPrinted} departmentName={registrationDepartmentName} moneyReady={!tokenPreparing} />
    )}
    <div style={card}>
      {/* ── Header ── */}
      <div style={{
        background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
        padding: '1rem 1.5rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
            {view !== 'mobile' && (
              <button onClick={handleBack} style={{
                background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
                cursor: 'pointer', fontSize: '0.875rem', padding: '0.25rem 0.5rem',
                borderRadius: '0.375rem', lineHeight: 1,
              }}>←</button>
            )}
            <span style={{ fontSize: '1.25rem' }}>📝</span>
            <h2 style={{ margin: 0, color: '#fff', fontWeight: 700, fontSize: '1.125rem' }}>Register Visit</h2>
          </div>
          {onCancel && (
            <button onClick={handleCancel} style={{
              background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
              cursor: 'pointer', fontSize: '0.875rem', padding: '0.25rem 0.625rem',
              borderRadius: '0.375rem',
            }}>✕</button>
          )}
        </div>
      </div>

      {/* ── Error Banner ── */}
      {error && (
        <div style={{
          padding: '0.75rem 1.5rem',
          background: isDark ? 'rgba(239,68,68,0.1)' : '#fef2f2',
          borderBottom: `1px solid ${isDark ? 'rgba(239,68,68,0.2)' : '#fecaca'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span>⚠️</span>
            <p style={{ margin: 0, fontSize: '0.875rem', color: isDark ? '#fca5a5' : '#991b1b' }}>{error}</p>
          </div>
          <button onClick={clearError} style={{
            background: 'none', border: 'none', color: isDark ? '#fca5a5' : '#ef4444',
            cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 500,
          }}>Dismiss</button>
        </div>
      )}

      {/* ── Doctor-collect block banner ── */}
      {blockError && (
        <div style={{
          padding: '0.75rem 1.5rem',
          background: isDark ? 'rgba(239,68,68,0.1)' : '#fef2f2',
          borderBottom: `1px solid ${isDark ? 'rgba(239,68,68,0.2)' : '#fecaca'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span>⚠️</span>
            <p style={{ margin: 0, fontSize: '0.875rem', color: isDark ? '#fca5a5' : '#991b1b' }}>{blockError}</p>
          </div>
          <button onClick={() => setBlockError('')} style={{
            background: 'none', border: 'none', color: isDark ? '#fca5a5' : '#ef4444',
            cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 500,
          }}>Dismiss</button>
        </div>
      )}

      {abhaDup.duplicate && (
        <div style={{ padding: '0.75rem 1.5rem' }}>
          <AbhaAlreadyLinkedNotice
            patientName={abhaDup.duplicate.patientName}
            uhid={abhaDup.duplicate.uhid}
            patientId={abhaDup.duplicate.patientId}
            onOpenPatient={handleOpenDuplicatePatient}
            opening={abhaDup.opening}
          />
        </div>
      )}

      {/* ── Body ── */}
      <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

        {/* ═══ STEP 1: Mobile Number ═══ */}
        <div>
          <label style={labelStyle}>Mobile Number *</label>
          <div style={{ position: 'relative' }}>
            <span style={{
              position: 'absolute', left: '0.875rem', top: '50%', transform: 'translateY(-50%)',
              fontSize: '0.9375rem', fontWeight: 500, color: isDark ? '#9ca3af' : '#6b7280',
            }}>+91</span>
            <input
              type="tel"
              inputMode="numeric"
              value={form.mobile}
              onChange={(e) => handleMobileChange(e.target.value)}
              placeholder="Enter 10-digit mobile"
              style={{ ...inputBase(validationErrors.mobile), paddingLeft: '3.25rem', fontSize: '1rem' }}
              maxLength={10}
              disabled={view !== 'mobile'}
            />
            {loading.patient && form.mobile.length === 10 && (
              <span style={{ position: 'absolute', right: '0.875rem', top: '50%', transform: 'translateY(-50%)' }}>
                <Spinner />
              </span>
            )}
            {view !== 'mobile' && (
              <button type="button"
                onClick={resetToMobile}
                style={{
                  position: 'absolute', right: '0.875rem', top: '50%', transform: 'translateY(-50%)',
                  background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'}`,
                  color: isDark ? '#d1d5db' : '#374151', cursor: 'pointer', fontSize: '0.75rem',
                  fontWeight: 500, padding: '0.25rem 0.625rem', borderRadius: '0.375rem',
                }}
              >Change</button>
            )}
          </div>
          {validationErrors.mobile && <p style={errorTextStyle}>{validationErrors.mobile}</p>}
        </div>

        {/* ═══ APPOINTMENT FOUND BANNER (within window — active) ═══ */}
        {detectedAppointment && detectedAppointment.within_window && (
          <div style={{
            padding: '0.75rem 1rem',
            borderRadius: '0.75rem',
            background: isDark ? 'rgba(34,197,94,0.08)' : 'rgba(34,197,94,0.06)',
            border: `1.5px solid ${isDark ? 'rgba(34,197,94,0.25)' : 'rgba(34,197,94,0.2)'}`,
            display: 'flex', alignItems: 'center', gap: '0.75rem',
          }}>
            <span style={{ fontSize: '1.5rem' }}>✅</span>
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontSize: '0.8125rem', fontWeight: 600, color: isDark ? '#86efac' : '#16a34a' }}>
                Appointment Found — #{detectedAppointment.appointment_number}
              </p>
              <p style={{ margin: '0.125rem 0 0', fontSize: '0.6875rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
                {detectedAppointment.patient_name} • Dr. {detectedAppointment.doctor_name || '—'} • {(() => {
                  const t = detectedAppointment.appointment_time;
                  if (!t) return '';
                  const m = /^(\d{1,2}):(\d{2})/.exec(t);
                  if (!m) return t;
                  const h = parseInt(m[1], 10);
                  return `${h % 12 || 12}:${m[2]} ${h >= 12 ? 'PM' : 'AM'}`;
                })()}
              </p>
            </div>
            <span style={{
              fontSize: '0.625rem', fontWeight: 600, color: '#22c55e',
              background: 'rgba(34,197,94,0.12)', padding: '0.1875rem 0.5rem',
              borderRadius: '0.25rem', whiteSpace: 'nowrap',
            }}>
              Active ✓
            </span>
          </div>
        )}

        {/* ═══ APPOINTMENT EXPIRED BANNER (out of window) ═══ */}
        {detectedAppointment && !detectedAppointment.within_window && !expiredDismissed && (
          <div style={{
            padding: '0.75rem 1rem',
            borderRadius: '0.75rem',
            background: isDark ? 'rgba(245,158,11,0.08)' : 'rgba(245,158,11,0.06)',
            border: `1.5px solid ${isDark ? 'rgba(245,158,11,0.25)' : 'rgba(245,158,11,0.2)'}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontSize: '1.5rem' }}>⚠️</span>
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontSize: '0.8125rem', fontWeight: 600, color: isDark ? '#fcd34d' : '#d97706' }}>
                  Appointment #{detectedAppointment.appointment_number} — Expired
                </p>
                <p style={{ margin: '0.125rem 0 0', fontSize: '0.6875rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
                  {detectedAppointment.patient_name} • Scheduled {(() => {
                    const t = detectedAppointment.appointment_time;
                    if (!t) return '';
                    const m = /^(\d{1,2}):(\d{2})/.exec(t);
                    if (!m) return t;
                    const h = parseInt(m[1], 10);
                    return `${h % 12 || 12}:${m[2]} ${h >= 12 ? 'PM' : 'AM'}`;
                  })()} — outside check-in window
                </p>
              </div>
              <span style={{
                fontSize: '0.625rem', fontWeight: 600, color: '#f59e0b',
                background: 'rgba(245,158,11,0.12)', padding: '0.1875rem 0.5rem',
                borderRadius: '0.25rem', whiteSpace: 'nowrap',
              }}>
                Expired
              </span>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.625rem' }}>
              <button type="button" onClick={() => {
                setExpiredDismissed(true);
                setForm((f) => ({ ...f, visitType: 'REGULAR', appointment_id: '' }));
              }} style={{
                flex: 1, padding: '0.5rem', borderRadius: '0.5rem', fontSize: '0.75rem', fontWeight: 600,
                border: 'none', cursor: 'pointer',
                background: isDark ? 'rgba(245,158,11,0.15)' : 'rgba(245,158,11,0.1)',
                color: isDark ? '#fcd34d' : '#d97706',
              }}>🚶 Continue as Walk-In</button>
              <button type="button" onClick={() => {
                setExpiredDismissed(true);
                clearDetected();
              }} style={{
                flex: 1, padding: '0.5rem', borderRadius: '0.5rem', fontSize: '0.75rem', fontWeight: 500,
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                background: 'transparent', cursor: 'pointer',
                color: isDark ? '#9ca3af' : '#6b7280',
              }}>✕ Dismiss</button>
            </div>
          </div>
        )}

        {/* ═══ STEP 2: Patient Selection ═══ */}
        {view === 'select' && patients.length > 0 && (
          <div>
            <label style={labelStyle}>Who is this visit for?</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
              {patients.map((p) => {
                const name = formatPatientName(p) || 'Unknown';
                const rel = p.relation || 'SELF';
                const relLabel = RELATIONS.find((r) => r.value === rel)?.label || rel;
                const ageText = patientAgeDisplay(p); // DOB wins, same as the summary (QA-ADDR-4)
                return (
                  <button key={p.patient_id} type="button" onClick={() => handleSelectPatient(p)} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '0.875rem 1.125rem', borderRadius: '0.75rem', width: '100%',
                    border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                    background: isDark ? 'rgba(255,255,255,0.04)' : '#fff',
                    cursor: 'pointer', transition: 'border-color 0.15s, background 0.15s',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <span style={{
                        width: '2.5rem', height: '2.5rem', borderRadius: '50%', flexShrink: 0,
                        background: 'linear-gradient(135deg, #0d9488, #14b8a6)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontSize: '0.875rem', fontWeight: 700,
                      }}>{(p.first_name || '?')[0].toUpperCase()}</span>
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '0.9375rem', fontWeight: 600, color: isDark ? '#f5f5f7' : '#111827' }}>{name}</span>
                          {/* ABHA health-identity state — same single-source badge as Book Appointment. */}
                          <AbhaBadge status={p.abha_status} isDark={isDark} />
                        </div>
                        <div style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : '#6b7280', marginTop: '0.125rem' }}>
                          {relLabel}{ageText ? ` · Age ${ageText}` : ''}{p.gender ? ` · ${p.gender}` : ''}
                        </div>
                        <AdmittedElsewhereLabel patient={p} />
                      </div>
                    </div>
                    <span style={{
                      padding: '0.375rem 1rem', borderRadius: '9999px', fontSize: '0.75rem',
                      fontWeight: 600, background: 'linear-gradient(135deg, #0d9488, #14b8a6)',
                      color: '#fff', flexShrink: 0,
                    }}>Confirm</span>
                  </button>
                );
              })}
            </div>
            <button type="button" onClick={handleAddFamilyMember} style={{ ...btnOutline, marginTop: '1rem' }}>
              <span>👥</span> Add Family Member
            </button>
          </div>
        )}

        {/* ═══ STEP 2b: New Patient Form ═══ */}
        {view === 'new_patient' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{
              padding: '0.625rem 1rem', borderRadius: '0.625rem',
              background: isDark ? 'rgba(20,184,166,0.08)' : '#f0fdfa',
              border: `1px solid ${isDark ? 'rgba(20,184,166,0.15)' : 'rgba(20,184,166,0.1)'}`,
              fontSize: '0.8125rem', color: '#0d9488', display: 'flex',
              alignItems: 'center', gap: '0.5rem', fontWeight: 500,
            }}>
              <span>👤</span>
              {patients.length > 0 ? 'Adding a family member to this number' : 'New patient registration'}
            </div>

            {abhaOldUhid && (
              <div style={{
                padding: '0.625rem 0.875rem', borderRadius: '0.625rem', fontSize: '0.8125rem',
                background: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#cbd5e1'}`,
                color: isDark ? '#d1d5db' : '#334155',
              }}>
                This will be a new patient with a new UHID. Old record ({abhaOldUhid}) is not changed.
              </div>
            )}
            {/* Name — 2 columns on desktop, stacks on mobile */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div>
                <label style={labelStyle}>First Name *</label>
                <input type="text" value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  disabled={isFirstNameLocked(abhaResult)}
                  placeholder="First name" style={{ ...inputBase(validationErrors.first_name), ...lockedInputStyle(isFirstNameLocked(abhaResult)) }} />
                {validationErrors.first_name && <p style={errorTextStyle}>{validationErrors.first_name}</p>}
              </div>
              <div>
                <label style={labelStyle}>Last Name {isLastNameRequired(abhaResult) ? '*' : ''}</label>
                <input type="text" value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  disabled={isLastNameLocked(abhaResult)}
                  placeholder="Last name"
                  style={{ ...inputBase(validationErrors.last_name), ...lockedInputStyle(isLastNameLocked(abhaResult)) }} />
                {validationErrors.last_name && <p style={errorTextStyle}>{validationErrors.last_name}</p>}
              </div>
            </div>

            {/* Relation chips */}
            <div>
              <label style={labelStyle}>Relation *</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {RELATIONS.map((r) => {
                  const selected = form.relation === r.value;
                  const disabled = r.value === 'SELF' && hasSelf;
                  return (
                    <button key={r.value} type="button" disabled={disabled}
                      onClick={() => !disabled && setForm({ ...form, relation: r.value })}
                      style={{
                        padding: '0.5rem 1rem', borderRadius: '9999px', fontSize: '0.8125rem',
                        fontWeight: 500, cursor: disabled ? 'not-allowed' : 'pointer',
                        opacity: disabled ? 0.35 : 1, transition: 'background 0.15s, color 0.15s, border-color 0.15s',
                        border: selected ? '2px solid #14b8a6' : `1.5px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
                        background: selected ? (isDark ? 'rgba(20,184,166,0.15)' : '#f0fdfa') : (isDark ? 'rgba(255,255,255,0.05)' : '#f9fafb'),
                        color: selected ? '#0d9488' : isDark ? '#e8e9ed' : '#6b7280',
                      }}
                    >{r.label}</button>
                  );
                })}
              </div>
              {relationHint && !form.relation && !validationErrors.relation && <p style={errorTextStyle}>{SELF_TAKEN_COPY}</p>}
              {validationErrors.relation && <p style={errorTextStyle}>{validationErrors.relation}</p>}
              {scanNotUsedNote && (
                <p data-testid="scan-not-used-note" style={{ fontSize: '0.75rem', color: isDark ? '#c7c9d1' : '#4b5563', marginTop: '0.5rem' }}>
                  Scan &amp; Share not used for this family member. Use Verify ABHA if they have their own ABHA.
                </p>
              )}
            </div>

            {/* Age, DOB, Gender — 3 columns on desktop, wraps on mobile */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem' }}>
              <div>
                <label style={labelStyle}>Age</label>
                {/* OR-127: a known DOB (ABHA's or typed) => age is calculated and read-only;
                    no DOB (incl. an ABHA birth year only, OR-134) => the typed age box. */}
                <AgeInput value={effectiveAge(form)}
                  onChange={(age) => setForm({ ...form, age })}
                  disabled={dobKnown}
                  inputStyle={{ ...inputBase(false), ...lockedInputStyle(dobKnown) }} />
                {dobKnown && <p data-testid="age-from-dob-note" style={ageNoteStyle}>{AGE_FROM_DOB_NOTE}</p>}
              </div>
              <div>
                <label style={labelStyle}>Date of Birth</label>
                <input type="date" value={form.dob}
                  onChange={(e) => setForm({ ...form, dob: e.target.value })}
                  disabled={abhaLocked(form.dob, 'dob')}
                  style={{ ...inputBase(false), ...lockedInputStyle(abhaLocked(form.dob, 'dob')) }} />
              </div>
              <div>
                <label style={labelStyle}>Gender</label>
                <select value={form.gender}
                  onChange={(e) => setForm({ ...form, gender: e.target.value })}
                  disabled={abhaLocked(form.gender, 'gender')}
                  style={{ ...inputBase(false), ...lockedInputStyle(abhaLocked(form.gender, 'gender')) }}>
                  <option value="">Select</option>
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
            </div>

            {/* Aadhaar */}
            <div>
              <label style={labelStyle}>Aadhaar Number</label>
              <MaskedAadhaarInput value={form.aadhaar_no}
                onChange={(e) => setForm({ ...form, aadhaar_no: e.target.value.replace(/\D/g, '').slice(0, 12) })}
                disabled={abhaLocked(form.aadhaar_no, 'aadhaar_no')}
                placeholder="12-digit Aadhaar" maxLength={12} style={inputBase(false)} isDark={isDark} />
            </div>

            {/* Address — Variant A row (OR-125). NOT ABHA-locked (OR-126): an ABHA address
                is filled in but stays editable, with an ABHA / Edited mark per field. */}
            <div>
              <AddressBlock
                value={addressValue}
                onChange={handleAddressChange}
                source={NO_SOURCE}
                abhaValues={abhaAddress}
                legacyAddress={null}
                mode="collapsed"
                idPrefix="rv-address"
                testIdPrefix="rv-address" />
              {validationErrors.address_pincode && <p style={errorTextStyle}>{validationErrors.address_pincode}</p>}
            </div>

            {/* Allergies — same label + placeholder the doctor's pad uses, so
                reception and the doctor read one field (ratified copy). */}
            <div>
              <label style={labelStyle}>Allergies</label>
              <input type="text" value={form.allergies}
                onChange={(e) => setForm({ ...form, allergies: e.target.value })}
                placeholder="e.g. Penicillin, Sulpha drugs" maxLength={500} style={inputBase(false)} />
            </div>

            <button type="button" onClick={handleConfirmNewPatient} style={btnPrimary(false)}>
              ✓ Confirm Patient Details
            </button>
          </div>
        )}

        {/* ═══ STEP 2c: Edit Patient Details ═══ */}
        {view === 'edit_patient' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{
              padding: '0.625rem 1rem', borderRadius: '0.625rem',
              background: isDark ? 'rgba(139,92,246,0.08)' : 'rgba(139,92,246,0.04)',
              border: `1px solid ${isDark ? 'rgba(139,92,246,0.2)' : 'rgba(139,92,246,0.12)'}`,
              fontSize: '0.8125rem', color: isDark ? '#c4b5fd' : '#7c3aed', display: 'flex',
              alignItems: 'center', gap: '0.5rem', fontWeight: 500,
            }}>
              <span>✏️</span> Edit Patient Details
            </div>

            {/* Name — 2 columns */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div>
                <label style={labelStyle}>First Name *</label>
                <input type="text" value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  disabled={abhaVerifiedExisting}
                  placeholder="First name" style={{ ...inputBase(validationErrors.first_name), ...lockedInputStyle(abhaVerifiedExisting) }} />
                {validationErrors.first_name && <p style={errorTextStyle}>{validationErrors.first_name}</p>}
              </div>
              <div>
                <label style={labelStyle}>Last Name *</label>
                <input type="text" value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  disabled={abhaVerifiedExisting}
                  placeholder="Last name"
                  style={{ ...inputBase(validationErrors.last_name), ...lockedInputStyle(abhaVerifiedExisting) }} />
                {validationErrors.last_name && <p style={errorTextStyle}>{validationErrors.last_name}</p>}
              </div>
            </div>

            {/* Relation chips — no SELF restriction in edit mode */}
            <div>
              <label style={labelStyle}>Relation *</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {RELATIONS.map((r) => {
                  const selected = form.relation === r.value;
                  return (
                    <button key={r.value} type="button"
                      disabled={abhaVerifiedExisting}
                      onClick={() => !abhaVerifiedExisting && setForm({ ...form, relation: r.value })}
                      style={{
                        padding: '0.5rem 1rem', borderRadius: '9999px', fontSize: '0.8125rem',
                        fontWeight: 500, cursor: abhaVerifiedExisting ? 'not-allowed' : 'pointer',
                        opacity: abhaVerifiedExisting && !selected ? 0.5 : 1,
                        transition: 'background 0.15s, color 0.15s, border-color 0.15s',
                        border: selected ? '2px solid #14b8a6' : `1.5px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
                        background: selected ? (isDark ? 'rgba(20,184,166,0.15)' : '#f0fdfa') : (isDark ? 'rgba(255,255,255,0.05)' : '#f9fafb'),
                        color: selected ? '#0d9488' : isDark ? '#e8e9ed' : '#6b7280',
                      }}
                    >{r.label}</button>
                  );
                })}
              </div>
              {validationErrors.relation && <p style={errorTextStyle}>{validationErrors.relation}</p>}
            </div>

            {/* Age, DOB, Gender */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem' }}>
              <div>
                <label style={labelStyle}>Age</label>
                <AgeInput value={effectiveAge(form)}
                  onChange={(age) => setForm({ ...form, age })}
                  disabled={abhaVerifiedExisting || dobKnown}
                  inputStyle={{ ...inputBase(false), ...lockedInputStyle(abhaVerifiedExisting || dobKnown) }} />
                {dobKnown && <p data-testid="age-from-dob-note" style={ageNoteStyle}>{AGE_FROM_DOB_NOTE}</p>}
              </div>
              <div>
                <label style={labelStyle}>Date of Birth</label>
                <input type="date" value={form.dob}
                  onChange={(e) => setForm({ ...form, dob: e.target.value })}
                  disabled={abhaVerifiedExisting}
                  style={{ ...inputBase(false), ...lockedInputStyle(abhaVerifiedExisting) }} />
              </div>
              <div>
                <label style={labelStyle}>Gender</label>
                <select value={form.gender}
                  onChange={(e) => setForm({ ...form, gender: e.target.value })}
                  disabled={abhaVerifiedExisting}
                  style={{ ...inputBase(false), ...lockedInputStyle(abhaVerifiedExisting) }}>
                  <option value="">Select</option>
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
            </div>

            {/* Aadhaar */}
            <div>
              <label style={labelStyle}>Aadhaar Number</label>
              <MaskedAadhaarInput value={form.aadhaar_no}
                onChange={(e) => setForm({ ...form, aadhaar_no: e.target.value.replace(/\D/g, '').slice(0, 12) })}
                disabled={abhaVerifiedExisting}
                placeholder="12-digit Aadhaar" maxLength={12} style={inputBase(false)} isDark={isDark} />
            </div>

            {/* Address — same block as the new-patient step. An existing patient's marks come
                from the server's per-field source map; a not-yet-saved patient compares
                against the ABHA address held in this form. */}
            <div>
              <AddressBlock
                value={addressValue}
                onChange={handleAddressChange}
                source={selectedPatient?.address_source || NO_SOURCE}
                abhaValues={selectedPatient ? null : abhaAddress}
                legacyAddress={legacyAddress}
                mode="collapsed"
                idPrefix="rv-address"
                testIdPrefix="rv-address" />
              {validationErrors.address_pincode && <p style={errorTextStyle}>{validationErrors.address_pincode}</p>}
            </div>

            {/* Allergies — same label + placeholder the doctor's pad uses, so
                reception and the doctor read one field (ratified copy). */}
            <div>
              <label style={labelStyle}>Allergies</label>
              <input type="text" value={form.allergies}
                onChange={(e) => setForm({ ...form, allergies: e.target.value })}
                placeholder="e.g. Penicillin, Sulpha drugs" maxLength={500} style={inputBase(false)} />
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button type="button" onClick={() => setView('visit')} style={{
                ...btnOutline, flex: 1,
              }}>
                Cancel
              </button>
              <button type="button" onClick={handleConfirmEdit} disabled={editSaving}
                style={{ ...btnPrimary(editSaving), flex: 2 }}>
                {editSaving ? '⏳ Saving...' : '✓ Save Changes'}
              </button>
            </div>
          </div>
        )}

        {/* ═══ STEP 3: Visit Details ═══ */}
        {view === 'visit' && (
          <form onSubmit={handleSubmit} style={{
            display: 'flex',
            flexDirection: (!isMobile && showFeeSection) ? 'row' : 'column',
            gap: (!isMobile && showFeeSection) ? '1rem' : '1.25rem',
            alignItems: (!isMobile && showFeeSection) ? 'flex-start' : 'stretch',
          }}>

            {/* ── Main column ── */}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

              {/* Patient summary */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem',
                padding: '1rem 1.125rem', borderRadius: '0.75rem',
                background: isDark ? 'rgba(20,184,166,0.08)' : '#f0fdfa',
                border: `1px solid ${isDark ? 'rgba(20,184,166,0.2)' : 'rgba(20,184,166,0.15)'}`,
              }}>
                <span style={{
                  width: '2.75rem', height: '2.75rem', borderRadius: '50%', flexShrink: 0,
                  background: 'linear-gradient(135deg, #0d9488, #14b8a6)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: '1rem', fontWeight: 700,
                }}>{(form.first_name || '?')[0].toUpperCase()}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '1rem', fontWeight: 600, color: isDark ? '#f5f5f7' : '#111827', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {patientName || (selectedPatient ? 'Existing patient' : 'New Patient')}
                    {selectedPatient && !selectedPatient.first_name && !selectedPatient.last_name && (
                      <span style={{ fontSize: '0.75rem', fontWeight: 400, color: isDark ? '#fbbf24' : '#b45309' }}>Patient details could not be loaded.</span>
                    )}
                    {isNurseVisit && (
                      <span style={{
                        fontSize: '0.5625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
                        padding: '0.125rem 0.4375rem', borderRadius: '9999px',
                        background: isDark ? 'rgba(20,184,166,0.2)' : 'rgba(20,184,166,0.12)',
                        color: isDark ? '#5eead4' : '#0d9488',
                        border: `1px solid ${isDark ? 'rgba(20,184,166,0.4)' : 'rgba(13,148,136,0.35)'}`,
                      }}>⚕ Nurse</span>
                    )}
                    {isLabVisit && (
                      <span style={{
                        fontSize: '0.5625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
                        padding: '0.125rem 0.4375rem', borderRadius: '9999px',
                        background: isDark ? 'rgba(139,92,246,0.2)' : 'rgba(139,92,246,0.12)',
                        color: isDark ? '#c4b5fd' : '#7c3aed',
                        border: `1px solid ${isDark ? 'rgba(139,92,246,0.4)' : 'rgba(124,58,237,0.35)'}`,
                      }}>🧪 {VISIT_TYPE_LABELS.LAB}</span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : '#6b7280', marginTop: '0.125rem' }}>
                    {RELATIONS.find((r) => r.value === form.relation)?.label || form.relation}
                    {effectiveAge(form) ? ` · Age ${effectiveAge(form)}` : ''}
                    {form.gender ? ` · ${form.gender}` : ''}
                    {' · '}{form.mobile}
                  </div>
                </div>
                {/* Q1 (OR-136): opens for an ABHA-verified patient too — there only address
                    + allergies are editable (the rest is locked in the Edit view). */}
                <button type="button" onClick={handleEditPatient}
                  style={{
                  flexShrink: 0, padding: '0.3rem 0.625rem', borderRadius: '0.5rem',
                  fontSize: '0.75rem', fontWeight: 600,
                  cursor: 'pointer',
                  border: `1.5px solid ${isDark ? 'rgba(139,92,246,0.3)' : 'rgba(139,92,246,0.2)'}`,
                  background: isDark ? 'rgba(139,92,246,0.1)' : 'rgba(139,92,246,0.06)',
                  color: isDark ? '#c4b5fd' : '#7c3aed',
                  display: 'flex', alignItems: 'center', gap: '0.25rem',
                  transition: 'background 0.15s, border-color 0.15s',
                }}>
                  ✏️ Edit
                </button>
                {selectedPatient && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.35rem', flexShrink: 0 }}>
                    {/* ABHA health-identity state (same single-source badge as Book Appointment). */}
                    {/* Reflect an in-session verify (abhaResult) immediately — the patient record's
                        abha_status is still stale until registration links it. */}
                    <AbhaBadge status={abhaResult ? 'VERIFIED' : selectedPatient.abha_status} isDark={isDark} />
                    <span style={{
                      padding: '0.25rem 0.625rem', borderRadius: '9999px',
                      fontSize: '0.6875rem', fontWeight: 600,
                      background: 'rgba(20,184,166,0.12)', color: '#14b8a6',
                    }}>Existing</span>
                    {scanLinkNote && <span data-testid="scan-link-note" style={{ fontSize: '0.6875rem' }}>{scanLinkNote}</span>}
                  </div>
                )}
              </div>

              {/* Provider — LAB-ASSIGN-2 / FE-3: locked to Lab for the
                  technician (§10.6 static line, no toggle rendered — the
                  Doctor/Nurse options aren't on the screen at all). Every
                  other role keeps the segmented toggle byte-identical. */}
              {isLabTechnician ? (
              <div>
                <label style={labelStyle}>Provider</label>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
                  padding: '0.625rem 1rem', borderRadius: '0.75rem', fontSize: '0.875rem', fontWeight: 600,
                  border: `1.5px solid ${isDark ? 'rgba(139,92,246,0.4)' : 'rgba(124,58,237,0.35)'}`,
                  background: isDark ? 'rgba(139,92,246,0.15)' : 'rgba(139,92,246,0.08)',
                  color: isDark ? '#c4b5fd' : '#7c3aed',
                }}><span>🧪</span>{LAB_VISIT_LABEL}</div>
              </div>
              ) : (
              <div>
                <label style={labelStyle}>Provider</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {[
                    { value: 'DOCTOR', label: 'Doctor (OPD)', icon: '🩺' },
                    { value: 'NURSE', label: providerCategory('NURSE')?.shortLabel || 'OTC', icon: '⚕' },
                    ...(labEnabled ? [{ value: 'LAB', label: LAB_VISIT_LABEL, icon: '🧪' }] : []),
                  ].map((opt) => {
                    const selected = form.visitProviderType === opt.value;
                    return (
                      <button key={opt.value} type="button"
                        onClick={() => handleProviderChange(opt.value)}
                        style={{
                          flex: 1,
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem',
                          padding: '0.625rem 1rem', borderRadius: '0.75rem', fontSize: '0.875rem',
                          fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s, color 0.2s, border-color 0.2s',
                          border: selected
                            ? '2px solid #0d9488'
                            : `1.5px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
                          background: selected
                            ? (isDark ? '#14b8a6' : '#0d9488')
                            : (isDark ? 'rgba(255,255,255,0.05)' : '#f9fafb'),
                          color: selected ? '#ffffff' : (isDark ? '#e8e9ed' : '#374151'),
                        }}
                      ><span>{opt.icon}</span>{opt.label}</button>
                    );
                  })}
                </div>
              </div>
              )}

              {/* Doctor — OPD only */}
              {form.visitProviderType === 'DOCTOR' && (
              <div>
                <label style={labelStyle}>Consulting Doctor *</label>
                <select value={form.visitDoctorId}
                  onChange={(e) => setForm({ ...form, visitDoctorId: e.target.value })}
                  style={inputBase(validationErrors.visitDoctorId)}>
                  <option value="">Select Doctor</option>
                  {/* S7 AC5 — when a department is picked first the list narrows
                      to that dept's doctors (filteredDoctors). */}
                  {filteredDoctors.map((doc) => (
                    <option key={doc.doctor_id} value={doc.doctor_id}>
                      Dr. {doc.name}{doc.specialization ? ` — ${SPECIALIZATION_LABELS[doc.specialization] || doc.specialization}` : ''}{doc.availability === 'on_hold' ? ' ⏸ On Hold' : doc.availability === 'closed_today' ? ' 🚫 Closed' : ''}
                    </option>
                  ))}
                </select>
                {validationErrors.visitDoctorId && <p style={errorTextStyle}>{validationErrors.visitDoctorId}</p>}
                {doctorPickerNotes()}
                {(() => {
                  if (!form.visitDoctorId) return null;
                  const doc = doctors.find((d) => d.doctor_id === form.visitDoctorId);
                  const avail = doc?.availability;
                  if (!avail || avail === 'available') return null;
                  const isHold = avail === 'on_hold';
                  const warningColor = isHold ? '#f59e0b' : '#ef4444';
                  const warningIcon = isHold ? '🟡' : '🔴';
                  const warningText = isHold
                    ? 'This doctor is currently On Hold. Patient may need to wait longer.'
                    : 'This doctor has Closed for Today. New registrations are not recommended.';
                  return (
                    <div style={{
                      marginTop: '0.375rem', padding: '0.4375rem 0.625rem', borderRadius: '0.5rem',
                      border: `1px solid ${isDark ? `${warningColor}30` : `${warningColor}25`}`,
                      background: isDark ? `${warningColor}10` : `${warningColor}08`,
                      display: 'flex', alignItems: 'flex-start', gap: '0.375rem',
                    }}>
                      <span style={{ fontSize: '0.75rem', flexShrink: 0, marginTop: '0.0625rem' }}>{warningIcon}</span>
                      <p style={{ margin: 0, fontSize: '0.75rem', lineHeight: 1.4, color: warningColor, fontWeight: 500 }}>{warningText}</p>
                    </div>
                  );
                })()}
              </div>
              )}

              {/* OTC — exactly one attendee: Nurse OR Doctor (W3 XOR). Sub-toggle
                  picks the kind; the matching picker below shows. Switching kind
                  clears the other id so the payload never carries both. */}
              {form.visitProviderType === 'NURSE' && (
              <div>
                <label style={labelStyle}>Attending Provider *</label>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.625rem' }}>
                  {OTC_ATTENDEE_KINDS.map((opt) => {
                    const selected = form.otcAttendeeKind === opt.value;
                    return (
                      <button key={opt.value} type="button"
                        onClick={() => handleOtcAttendeeKind(opt.value)}
                        style={{
                          flex: 1,
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem',
                          padding: '0.5rem 0.75rem', borderRadius: '0.625rem', fontSize: '0.8125rem',
                          fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s, color 0.2s, border-color 0.2s',
                          border: selected
                            ? '2px solid #0d9488'
                            : `1.5px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
                          background: selected
                            ? (isDark ? '#14b8a6' : '#0d9488')
                            : (isDark ? 'rgba(255,255,255,0.05)' : '#f9fafb'),
                          color: selected ? '#ffffff' : (isDark ? '#e8e9ed' : '#374151'),
                        }}
                      ><span>{opt.icon}</span>{opt.label}</button>
                    );
                  })}
                </div>

                {form.otcAttendeeKind === 'NURSE' ? (
                  <>
                    <select value={form.visitNurseId}
                      onChange={(e) => setForm({ ...form, visitNurseId: e.target.value })}
                      style={inputBase(validationErrors.otcAttendee)}>
                      <option value="">Select Nurse</option>
                      {/* S8 AC5 — when a department is picked first the list narrows
                          to that dept's nurses (filteredNurses). */}
                      {filteredNurses.map((n) => {
                        const nm = `${(n.first_name || '').trim()} ${(n.last_name || '').trim()}`.trim() || 'Nurse';
                        const inactive = String(n.status || '').toUpperCase() !== 'ACTIVE';
                        return (
                          <option key={n.member_id} value={n.member_id}>
                            {nm}{inactive && n.status ? ` [${n.status}]` : ''}
                          </option>
                        );
                      })}
                    </select>
                    {form.department_id && filteredNurses.length === 0 && viewerNurses.length > 0 && (
                      <p style={{ margin: '0.375rem 0 0', fontSize: '0.8125rem', color: isDark ? '#6b7280' : '#9ca3af' }}>
                        No nurses in the selected department. Change the department to see more.
                      </p>
                    )}
                    {nursesLoading && <p style={mutedNoteStyle}>{NURSES_LOADING_COPY}</p>}
                    {nursesError && <p style={errorTextStyle}>{NURSES_LOAD_ERROR_COPY}</p>}
                    {!nursesLoading && !nursesError && viewerNurses.length === 0 && <p style={{ margin: '0.375rem 0 0', fontSize: '0.8125rem', color: isDark ? '#6b7280' : '#9ca3af' }}>No nurses found in this organisation.</p>}
                  </>
                ) : (
                  <>
                    <select value={form.visitDoctorId}
                      onChange={(e) => setForm({ ...form, visitDoctorId: e.target.value })}
                      style={inputBase(validationErrors.otcAttendee)}>
                      <option value="">Select Doctor</option>
                      {/* Doctor list is the existing doctors source, dept-filtered
                          just like nurses are (filteredDoctors == filteredProviders
                          for the OTC-doctor leg). */}
                      {filteredDoctors.map((doc) => (
                        <option key={doc.doctor_id} value={doc.doctor_id}>
                          Dr. {doc.name}{doc.specialization ? ` — ${SPECIALIZATION_LABELS[doc.specialization] || doc.specialization}` : ''}
                        </option>
                      ))}
                    </select>
                    {doctorPickerNotes()}
                  </>
                )}
                {validationErrors.otcAttendee && <p style={errorTextStyle}>{validationErrors.otcAttendee}</p>}
              </div>
              )}

              {/* Lab Technician — Lab Visit only (doctor-less walk-in test) */}
              {form.visitProviderType === 'LAB' && (
              <div>
                <label style={labelStyle}>Attending Lab Technician *</label>
                <select value={form.visitLabTechId}
                  onChange={(e) => setForm({ ...form, visitLabTechId: e.target.value })}
                  style={inputBase(validationErrors.visitLabTechId)}>
                  <option value="">Select Lab Technician</option>
                  {/* S8 AC5 — when a department is picked first the list narrows
                      to that dept's lab technicians (filteredLabTechs). */}
                  {filteredLabTechs.map((t) => {
                    const nm = `${(t.first_name || '').trim()} ${(t.last_name || '').trim()}`.trim() || 'Lab Technician';
                    const inactive = String(t.status || '').toUpperCase() !== 'ACTIVE';
                    return (
                      <option key={t.member_id} value={t.member_id}>
                        {nm}{inactive && t.status ? ` [${t.status}]` : ''}
                      </option>
                    );
                  })}
                </select>
                {validationErrors.visitLabTechId && <p style={errorTextStyle}>{validationErrors.visitLabTechId}</p>}
                {/* LAB-ASSIGN-2 / FE-3 — self pre-select helper (§10.6, frozen copy). */}
                {isLabTechnician && (
                  <p style={{ margin: '0.375rem 0 0', fontSize: '0.8125rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                    Assigned to you. Change it if someone else will run the test.
                  </p>
                )}
                {form.department_id && filteredLabTechs.length === 0 && viewerLabTechs.length > 0 && (
                  <p style={{ margin: '0.375rem 0 0', fontSize: '0.8125rem', color: isDark ? '#6b7280' : '#9ca3af' }}>
                    No lab technicians in the selected department. Change the department to see more.
                  </p>
                )}
                {labTechsLoading && <p style={mutedNoteStyle}>{LAB_TECHS_LOADING_COPY}</p>}
                {labTechsError && <p style={errorTextStyle}>{LAB_TECHS_LOAD_ERROR_COPY}</p>}
                {!labTechsLoading && !labTechsError && viewerLabTechs.length === 0 && <p style={{ margin: '0.375rem 0 0', fontSize: '0.8125rem', color: isDark ? '#6b7280' : '#9ca3af' }}>No lab technicians found in this organisation.</p>}
              </div>
              )}

              {/* Visit type chips — OPD only (a Nurse / Lab Visit's type is fixed) */}
              {form.visitProviderType === 'DOCTOR' && (
              <div>
                <label style={labelStyle}>Visit Type</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {VISIT_TYPES.map((vt) => {
                    const selected = form.visitType === vt.value;
                    const isEmergency = vt.value === 'EMERGENCY';
                    return (
                      <button key={vt.value} type="button"
                        onClick={() => handleVisitTypeChange(vt.value)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
                          padding: '0.5rem 1rem', borderRadius: '9999px', fontSize: '0.8125rem',
                          fontWeight: 500, cursor: 'pointer', transition: 'background 0.2s, color 0.2s, border-color 0.2s',
                          border: selected
                            ? `2px solid ${isEmergency ? '#ef4444' : '#14b8a6'}`
                            : `1.5px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
                          background: selected
                            ? isEmergency ? (isDark ? 'rgba(239,68,68,0.15)' : '#fef2f2') : (isDark ? 'rgba(20,184,166,0.15)' : '#f0fdfa')
                            : isDark ? 'rgba(255,255,255,0.05)' : '#f9fafb',
                          color: selected ? (isEmergency ? '#ef4444' : '#0d9488') : isDark ? '#e8e9ed' : '#6b7280',
                        }}
                      ><span>{vt.icon}</span>{vt.label}</button>
                    );
                  })}
                </div>
              </div>
              )}

              {/* Department (EPIC A / A2, S7 + S8) — dept-of-care stamped at
                  registration, derived from the SELECTED ACTIVE ATTENDING PROVIDER
                  (doctor OPD / nurse / lab tech). Shown when the org has
                  departments AND the visit isn't Emergency (server assigns the
                  EMERGENCY sentinel). LOCKED (read-only) when a single-dept
                  provider auto-fills; CONSTRAINED to the provider's depts when
                  multi; FULL org list when the provider has none / no provider is
                  set. Options come from deptResolution (single source). */}
              {deptResolution.showPicker && (
              <div>
                <label style={labelStyle}>Department {deptResolution.required ? '*' : ''}</label>
                <select value={form.department_id}
                  disabled={deptResolution.locked}
                  onChange={(e) => {
                    const val = e.target.value;
                    // A dept picked while NO active provider is set is a MANUAL
                    // pick — retained on a later provider-clear (AC7). Once a
                    // provider is set the value is provider-derived, not manual.
                    manualDeptPickRef.current = !hasActiveProvider && !!val;
                    setForm((f) => ({ ...f, department_id: val }));
                  }}
                  style={{
                    ...inputBase(validationErrors.department_id),
                    ...(deptResolution.locked
                      ? { opacity: 0.85, cursor: 'not-allowed', background: isDark ? 'rgba(255,255,255,0.03)' : '#eef2f7' }
                      : {}),
                  }}>
                  <option value="">Select Department</option>
                  {deptResolution.options.map((d) => {
                    const did = d.dept_id || d.id;
                    return <option key={did} value={did}>{d.name}</option>;
                  })}
                </select>
                {deptResolution.locked && (
                  <p style={{ margin: '0.375rem 0 0', fontSize: '0.75rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
                    Set by the selected provider’s department.
                  </p>
                )}
                {validationErrors.department_id && <p style={errorTextStyle}>{validationErrors.department_id}</p>}
              </div>
              )}

              {/* Procedures-on-New-Visits — pending suggestions + catalog adds.
                  In OTC mode the chosen nurse is the default assignee.
                  Hidden on a Lab Visit — that surfaces Investigations instead. */}
              {caps.procedures && (
              <RegistrationProceduresSection
                patientId={form.patient_id}
                departmentId={form.department_id}
                visitProviderType={form.visitProviderType}
                doctorId={form.visitDoctorId}
                doctorName={doctors.find((d) => d.doctor_id === form.visitDoctorId)?.name}
                defaultNurseId={isNurseVisit ? form.visitNurseId : ''}
                appointment={detectedAppointment}
                onChange={handleProceduresChange}
              />
              )}

              {/* Lab Visit — investigations (lab tests) ordered on this visit;
                  each becomes a LabOrder assigned to the technician. Only shown
                  to users who can order labs (ORDER_LAB: doctors/admins today). */}
              {caps.lab && canOrderLab && (
                <RegistrationInvestigationsSection
                  patientId={form.patient_id}
                  departmentId={form.department_id}
                  onChange={handleInvestigationsChange}
                />
              )}

              {/* BUSY-VOUCHER R1 (OR-42/45/46/48) — optional "Advised by doctor",
                  directly below Investigations, Lab & Diagnostic visits only
                  (every role that can register one, incl. the technician — NOT
                  gated on canOrderLab). Doctors = the org-wide roster already
                  loaded above (no extra request). */}
              {isLabVisit && (
                <AdvisedByDoctorField
                  doctors={doctors}
                  doctorId={form.advisedByDoctorId}
                  typedName={form.advisedByTypedName}
                  onChange={({ doctorId, typedName }) => setForm((f) => ({ ...f, advisedByDoctorId: doctorId, advisedByTypedName: typedName }))}
                  isDark={isDark}
                />
              )}

              {feeWaiverNote && (
                <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: isDark ? '#5eead4' : '#0d9488' }}>
                  ✓ {feeWaiverNote} — consultation fee waived
                </div>
              )}

              {/* Mobile: compact inline fee card */}
              {isMobile && showFeeSection && (
                <div style={{
                  borderRadius: '0.75rem',
                  border: `1px solid ${isDark ? 'rgba(20,184,166,0.2)' : 'rgba(20,184,166,0.18)'}`,
                  background: isDark ? 'rgba(20,184,166,0.05)' : 'rgba(240,253,250,0.7)',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    padding: '0.4375rem 0.875rem',
                    background: isDark ? 'rgba(20,184,166,0.1)' : 'rgba(20,184,166,0.08)',
                    borderBottom: `1px solid ${isDark ? 'rgba(20,184,166,0.15)' : 'rgba(20,184,166,0.1)'}`,
                    display: 'flex', alignItems: 'center', gap: '0.375rem',
                  }}>
                    <span style={{ fontSize: '0.75rem' }}>💰</span>
                    <span style={{ fontSize: '0.6875rem', fontWeight: 700, color: isDark ? '#5eead4' : '#0d9488', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Fees</span>
                  </div>
                  <div style={{ padding: '0.75rem 0.875rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: (showConsultFee && showRegFee) ? '1fr 1fr' : '1fr', gap: '0.625rem' }}>
                      {showConsultFee && (
                        <div>
                          <label style={{ display: 'block', fontSize: '0.6875rem', fontWeight: 600, color: isDark ? '#9ca3af' : '#6b7280', marginBottom: '0.25rem' }}>Consultation (₹)</label>
                          <input type="number" min="0" step="1" placeholder="0" value={consultationFee}
                            onChange={(e) => setConsultationFee(e.target.value)}
                            style={{
                              width: '100%', padding: '0.4375rem 0.5625rem', borderRadius: '0.5rem', boxSizing: 'border-box',
                              border: `1.5px solid ${isDark ? 'rgba(20,184,166,0.25)' : 'rgba(20,184,166,0.22)'}`,
                              background: isDark ? 'rgba(255,255,255,0.06)' : '#fff',
                              color: isDark ? '#f5f5f7' : '#111827', fontSize: '0.9375rem', fontWeight: 600, outline: 'none',
                            }} />
                        </div>
                      )}
                      {showRegFee && (
                        <div>
                          <label style={{ display: 'block', fontSize: '0.6875rem', fontWeight: 600, color: isDark ? '#9ca3af' : '#6b7280', marginBottom: '0.25rem' }}>
                            Reg. Fee (₹){' '}
                            <span style={{ color: '#14b8a6', fontWeight: 700 }}>· New</span>
                          </label>
                          <input type="number" min="0" step="1" placeholder="0" value={registrationFee}
                            onChange={(e) => setRegistrationFee(e.target.value)}
                            style={{
                              width: '100%', padding: '0.4375rem 0.5625rem', borderRadius: '0.5rem', boxSizing: 'border-box',
                              border: `1.5px solid ${isDark ? 'rgba(20,184,166,0.25)' : 'rgba(20,184,166,0.22)'}`,
                              background: isDark ? 'rgba(255,255,255,0.06)' : '#fff',
                              color: isDark ? '#f5f5f7' : '#111827', fontSize: '0.9375rem', fontWeight: 600, outline: 'none',
                            }} />
                        </div>
                      )}
                    </div>
                    {investigationFeeTotal > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 500 }}>Investigations</span>
                        <span style={{ fontSize: '0.9375rem', fontWeight: 700, color: isDark ? '#f5f5f7' : '#111827' }}>
                          ₹{investigationFeeTotal.toLocaleString('en-IN')}
                        </span>
                      </div>
                    )}
                    <div style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      paddingTop: '0.4375rem',
                      borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}`,
                    }}>
                      <span style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 500 }}>Total</span>
                      <span style={{ fontSize: '1.0625rem', fontWeight: 700, color: isDark ? '#5eead4' : '#0d9488' }}>
                        ₹{feeTotal.toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Mode */}
              <div>
                <label style={labelStyle}>Payment Mode *</label>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {PAYMENT_MODES.map((pm) => {
                    const selected = form.paymentMode === pm.id;
                    return (
                      <button key={pm.id} type="button"
                        onClick={() => setForm({ ...form, paymentMode: pm.id })}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
                          padding: '0.5rem 1.125rem', borderRadius: '9999px', fontSize: '0.8125rem',
                          fontWeight: 500, cursor: 'pointer', transition: 'background 0.2s, border-color 0.2s',
                          border: selected
                            ? '2px solid #14b8a6'
                            : `1.5px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
                          background: selected
                            ? isDark ? 'rgba(20,184,166,0.15)' : '#f0fdfa'
                            : isDark ? 'rgba(255,255,255,0.05)' : '#f9fafb',
                          color: selected ? '#0d9488' : isDark ? '#e8e9ed' : '#6b7280',
                        }}
                      >
                        <span>{pm.icon}</span>{pm.label}
                      </button>
                    );
                  })}
                </div>
                {validationErrors.paymentMode && <p style={errorTextStyle}>{validationErrors.paymentMode}</p>}
              </div>

              {/* CONSENT-CAPTURE P1 (D13) — immediately above the Register CTA. */}
              <ConsentBlock
                catalogEntry={consentCatalogEntry}
                checked={form.consentGranted}
                onChange={(next) => setForm((f) => ({ ...f, consentGranted: next }))}
                existingEntry={digitalRecordsEntry}
              />

              {/* D15.1 — a failed/unavailable catalogue GET blocks Register just
                  like an untick; never fabricate consent to text nobody saw. */}
              {(() => {
                const registerDisabled = loading.register || !form.consentGranted || !consentCatalogEntry;
                return (
                  <>
                    <button
                      type="submit"
                      disabled={registerDisabled}
                      style={btnPrimary(registerDisabled)}
                      data-testid="register-visit-submit"
                    >
                      {loading.register ? (<><Spinner /> Registering...</>) : isNurseVisit ? (<>⚕ Register OTC Visit & Assign Queue</>) : isLabVisit ? (<>{`🧪 Register ${LAB_VISIT_LABEL} & Assign Queue`}</>) : (<>📋 Register Visit & Assign Queue</>)}
                    </button>
                    {!consentCatalogEntry ? (
                      <p
                        style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : '#6b7280', marginTop: '-0.75rem' }}
                        data-testid="consent-required-reason"
                      >
                        Consent text unavailable — retry.
                      </p>
                    ) : !form.consentGranted && (
                      <p
                        style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : '#6b7280', marginTop: '-0.75rem' }}
                        data-testid="consent-required-reason"
                      >
                        Consent is required to register this visit.
                      </p>
                    )}
                  </>
                );
              })()}
            </div>

            {/* ── Fee side panel (desktop only) ── */}
            {!isMobile && showFeeSection && (
              <div style={{
                width: '196px',
                flexShrink: 0,
                alignSelf: 'flex-start',
                position: 'sticky',
                top: 0,
                borderRadius: '0.875rem',
                border: `1px solid ${isDark ? 'rgba(20,184,166,0.2)' : 'rgba(20,184,166,0.18)'}`,
                background: isDark ? 'rgba(20,184,166,0.05)' : 'rgba(240,253,250,0.6)',
                overflow: 'hidden',
              }}>
                {/* Panel header */}
                <div style={{
                  padding: '0.625rem 0.875rem',
                  background: isDark ? 'rgba(20,184,166,0.1)' : 'rgba(20,184,166,0.09)',
                  borderBottom: `1px solid ${isDark ? 'rgba(20,184,166,0.15)' : 'rgba(20,184,166,0.12)'}`,
                  display: 'flex', alignItems: 'center', gap: '0.375rem',
                }}>
                  <span style={{ fontSize: '0.875rem' }}>💰</span>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: isDark ? '#5eead4' : '#0d9488' }}>Fees</span>
                </div>

                {/* Fee inputs */}
                <div style={{ padding: '0.875rem', display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
                  {showConsultFee && (
                    <div>
                      <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: isDark ? '#9ca3af' : '#6b7280', marginBottom: '0.3125rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Consultation
                      </div>
                      <div style={{ position: 'relative' }}>
                        <span style={{
                          position: 'absolute', left: '0.5625rem', top: '50%', transform: 'translateY(-50%)',
                          fontSize: '0.8125rem', color: isDark ? '#9ca3af' : '#9ca3af', fontWeight: 500, pointerEvents: 'none',
                        }}>₹</span>
                        <input type="number" min="0" step="1" placeholder="0" value={consultationFee}
                          onChange={(e) => setConsultationFee(e.target.value)}
                          style={{
                            width: '100%', paddingLeft: '1.375rem', paddingRight: '0.5rem',
                            paddingTop: '0.5rem', paddingBottom: '0.5rem',
                            borderRadius: '0.5rem', boxSizing: 'border-box',
                            border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                            background: isDark ? 'rgba(255,255,255,0.07)' : '#fff',
                            color: isDark ? '#f5f5f7' : '#111827',
                            fontSize: '1rem', fontWeight: 700, outline: 'none',
                          }} />
                      </div>
                    </div>
                  )}

                  {showRegFee && (
                    <div>
                      <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: isDark ? '#9ca3af' : '#6b7280', marginBottom: '0.3125rem', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '0.3125rem' }}>
                        Registration
                        <span style={{
                          fontSize: '0.5625rem', fontWeight: 700, color: '#14b8a6',
                          background: isDark ? 'rgba(20,184,166,0.15)' : 'rgba(20,184,166,0.12)',
                          padding: '0.0625rem 0.3125rem', borderRadius: '9999px',
                          textTransform: 'none', letterSpacing: 0,
                        }}>New</span>
                      </div>
                      <div style={{ position: 'relative' }}>
                        <span style={{
                          position: 'absolute', left: '0.5625rem', top: '50%', transform: 'translateY(-50%)',
                          fontSize: '0.8125rem', color: isDark ? '#9ca3af' : '#9ca3af', fontWeight: 500, pointerEvents: 'none',
                        }}>₹</span>
                        <input type="number" min="0" step="1" placeholder="0" value={registrationFee}
                          onChange={(e) => setRegistrationFee(e.target.value)}
                          style={{
                            width: '100%', paddingLeft: '1.375rem', paddingRight: '0.5rem',
                            paddingTop: '0.5rem', paddingBottom: '0.5rem',
                            borderRadius: '0.5rem', boxSizing: 'border-box',
                            border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                            background: isDark ? 'rgba(255,255,255,0.07)' : '#fff',
                            color: isDark ? '#f5f5f7' : '#111827',
                            fontSize: '1rem', fontWeight: 700, outline: 'none',
                          }} />
                      </div>
                    </div>
                  )}

                  {investigationFeeTotal > 0 && (
                    <div>
                      <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: isDark ? '#9ca3af' : '#6b7280', marginBottom: '0.3125rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Investigations
                      </div>
                      <div style={{ fontSize: '1.125rem', fontWeight: 700, color: isDark ? '#f5f5f7' : '#111827' }}>
                        ₹{investigationFeeTotal.toLocaleString('en-IN')}
                      </div>
                    </div>
                  )}

                  {/* Divider */}
                  <div style={{ height: '1px', background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)' }} />

                  {/* Total */}
                  <div>
                    <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: isDark ? '#9ca3af' : '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.1875rem' }}>
                      Total
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 800, color: isDark ? '#5eead4' : '#0d9488', letterSpacing: '-0.02em', lineHeight: 1 }}>
                      ₹{feeTotal.toLocaleString('en-IN')}
                    </div>
                  </div>
                </div>
              </div>
            )}

          </form>
        )}
      </div>
    </div>
    </>
  );
}


/* ═══════════════════════════════════════════════════════════════════
   SuccessPopup — Full-screen queue confirmation overlay
   Auto-redirects to register page after 5 seconds.
   Close button also redirects to register page.
   ═══════════════════════════════════════════════════════════════════ */
export function SuccessPopup({
  data, isDark, onClose, isScoped, branding, amountPaid, lineItems, receiptNumber, receiptNumberPrinted, departmentName,
  // F13 — false while the token's money is still being derived from the collect
  // response (see handleSubmit). Gates the Print button + pauses auto-close so a
  // fast click / slow network can never print an empty or wrong-amount token.
  // Defaults true so the isolated SuccessPopup unit tests (which pass a ready
  // amount directly) are unaffected.
  moneyReady = true,
}) {
  const visit = data.visit || {};
  const queueNumber = data.queue_number || visit.queue_number || '—';
  const patientName = visit.patient_name || `${data.patient?.first_name || ''} ${data.patient?.last_name || ''}`.trim() || '—';
  const doctorName = visit.doctor_name || '—';
  const status = visit.status || 'QUEUE';
  const visitType = visit.visit_type || '—';
  const isNew = data.is_new_patient;
  const relation = data.patient?.relation;
  const relLabel = relation ? (RELATIONS.find((r) => r.value === relation)?.label || relation) : null;
  const appointmentNumber = visit.appointment_number || data.appointment_number || null;

  // Token: #queue#appointment when appointment-linked, otherwise just #queue
  const tokenDisplay = appointmentNumber
    ? `#${queueNumber}#${appointmentNumber}`
    : `#${queueNumber}`;

  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    // F13 — hold the auto-close countdown until the token money is ready, so a
    // slow collect can't dismiss the popup before the owner can print.
    if (!moneyReady) return undefined;
    if (countdown <= 0) {
      onClose();
      return undefined;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, onClose, moneyReady]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: isDark ? 'rgba(0,0,0,0.75)' : 'rgba(0,0,0,0.5)',
      padding: '1rem',
      overflowY: 'auto',
    }}>
      {/* ✕ Close — floating outside the card so it's never clipped */}
      <button onClick={onClose} style={{
        position: 'fixed', top: '1rem', right: '1rem', zIndex: 10000,
        background: isDark ? '#374151' : '#ffffff',
        border: `2px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'}`,
        color: isDark ? '#f5f5f7' : '#374151',
        cursor: 'pointer', fontSize: '1.25rem', fontWeight: 700,
        width: '2.75rem', height: '2.75rem', borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.5)' : '0 4px 16px rgba(0,0,0,0.15)',
        transition: 'background 0.15s, transform 0.15s',
      }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
        title="Close & go back"
      >✕</button>

      <div onClick={(e) => e.stopPropagation()} style={{
        width: '100%', maxWidth: '480px', borderRadius: '1.5rem',
        background: isDark ? '#1a1a22' : '#ffffff',
        boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 50%, #2dd4bf 100%)',
          padding: '2rem 1.5rem 1.75rem', textAlign: 'center',
          borderRadius: '1.5rem 1.5rem 0 0',
        }}>

          <div style={{
            width: '4rem', height: '4rem', margin: '0 auto 1rem', borderRadius: '50%',
            background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: '2rem',
          }}>✅</div>
          <h3 style={{ margin: 0, color: '#fff', fontSize: '1.375rem', fontWeight: 700 }}>
            Visit Registered Successfully!
          </h3>
          {isNew && (
            <span style={{
              display: 'inline-block', marginTop: '0.75rem', padding: '0.25rem 0.875rem',
              borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 600,
              background: 'rgba(255,255,255,0.2)', color: '#fff',
            }}>New Patient Created</span>
          )}
        </div>

        {/* Queue Number / Token — large, centered, no cutoff */}
        <div style={{ textAlign: 'center', padding: '1.75rem 1.5rem 1.25rem' }}>
          <p style={{
            margin: 0, fontSize: '0.8125rem', fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.12em', color: isDark ? 'rgba(255,255,255,0.4)' : '#9ca3af',
          }}>{appointmentNumber ? 'Token' : 'Queue Number'}</p>
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            marginTop: '0.75rem', padding: '0.75rem 2.5rem', borderRadius: '1rem',
            background: isDark ? 'rgba(20,184,166,0.1)' : 'rgba(20,184,166,0.06)',
            border: `2px solid ${isDark ? 'rgba(20,184,166,0.25)' : 'rgba(20,184,166,0.15)'}`,
          }}>
            <span style={{
              fontSize: appointmentNumber ? 'clamp(2rem, 5vw, 2.75rem)' : 'clamp(2.5rem, 6vw, 3.5rem)',
              fontWeight: 800,
              fontFamily: '"SF Mono", "Cascadia Code", "Fira Code", "Consolas", monospace',
              background: 'linear-gradient(135deg, #0d9488, #14b8a6)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              lineHeight: 1.2, whiteSpace: 'nowrap',
            }}>{tokenDisplay}</span>
          </div>
          {appointmentNumber && (
            <p style={{
              margin: '0.5rem 0 0', fontSize: '0.6875rem',
              color: isDark ? 'rgba(255,255,255,0.35)' : '#9ca3af',
            }}>
              Queue #{queueNumber} · Appointment #{appointmentNumber}
            </p>
          )}
        </div>

        {/* Details */}
        <div style={{ padding: '0 1.5rem 1.25rem' }}>
          <div style={{
            borderRadius: '0.75rem',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
            background: isDark ? 'rgba(255,255,255,0.03)' : '#f9fafb', overflow: 'hidden',
          }}>
            <PopupRow label="Patient" value={patientName} isDark={isDark} />
            {relLabel && <PopupRow label="Relation" value={relLabel} isDark={isDark} border />}
            <PopupRow label="Doctor" value={`Dr. ${doctorName}`} isDark={isDark} border />
            <PopupRow label="Status" value={status} isDark={isDark} border badge />
            <PopupRow label="Visit Type" value={VISIT_TYPE_LABELS[visitType] || visitType.replace(/_/g, ' ')} isDark={isDark} border />
          </div>
        </div>

        {/* Print Token */}
        <div style={{ padding: '0 1.5rem 0.75rem' }}>
          <button
            // F13 — never print until the token money is derived from the
            // collect response (moneyReady). Prevents a fast click printing an
            // empty / wrong-amount token before the collect calls resolve.
            disabled={!moneyReady}
            onClick={!moneyReady ? undefined : () => printToken({
              // F16/W7 — the minted Receipt No. folded from the collect response
              // (registrationReceiptNumber); the register response's own
              // `visit.receipt_number` is NULL post-W7 (numbers mint at payment)
              // and stays the fallback for an already-numbered / legacy visit.
              receipt_number: receiptNumber ?? visit.receipt_number,
              // GST-DOCNO/A2-d (R-GD-10) — the server-stamped document number
              // for the SAME receipt_number above; undefined when we fell
              // back to `visit.receipt_number` (no printed sibling in this
              // contract for that legacy field) — printToken then falls back
              // to the bare int, byte-identical to today.
              receipt_number_printed: receiptNumber != null ? receiptNumberPrinted : undefined,
              queue_number: queueNumber,
              patient: data.patient,
              payment_mode: visit.payment_mode,
              visit_time: visit.visit_time,
              visit_date: visit.visit_date || visit.created_at,
              // Total collected across every canonical Payment on the visit
              // (consultation + registration + lab + procedure), derived in
              // handleSubmit from the collect RESPONSE the client holds — never
              // the local form intent, never a read-back that can race
              // (GATE 3 RULING 2 / RC5 / F13).
              amount_paid: amountPaid,
              line_items: lineItems || undefined,
              // F2 — Department row on the token (omitted when null/no dept).
              department_name: departmentName || undefined,
              clinic_name: branding.name,
              clinic_description: branding.description,
              address: branding.address,
              pincode: branding.pincode,
              phone: branding.phone,
              // Doctor row + Advised-by + assignee (Nurse/Lab) from the visit.
              ...tokenProviderFields(visit),
            })}
            style={{
              width: '100%', padding: '0.75rem', borderRadius: '0.75rem',
              border: `1.5px solid ${isDark ? 'rgba(20,184,166,0.3)' : 'rgba(20,184,166,0.25)'}`,
              background: isDark ? 'rgba(20,184,166,0.08)' : 'rgba(20,184,166,0.05)',
              color: isDark ? '#2dd4bf' : '#0d9488',
              fontWeight: 600, fontSize: '0.9375rem',
              cursor: moneyReady ? 'pointer' : 'not-allowed',
              opacity: moneyReady ? 1 : 0.6,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
            }}
          >
            {moneyReady ? '🖨️ Print Token' : '⏳ Preparing receipt…'}
          </button>
        </div>

        {/* Close + Auto-redirect */}
        <div style={{ padding: '0 1.5rem 1.75rem' }}>
          <button onClick={onClose} style={{
            width: '100%', padding: '1rem', borderRadius: '0.75rem', border: 'none',
            background: 'linear-gradient(135deg, #0d9488, #14b8a6)', color: '#fff',
            fontWeight: 600, fontSize: '1rem', cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(13, 148, 136, 0.35)',
          }}>
            {isScoped ? 'Close & Go to Dashboard' : 'Close & Register Next Visit'}
          </button>
          <p style={{
            textAlign: 'center', margin: '0.75rem 0 0', fontSize: '0.8125rem',
            color: isDark ? 'rgba(255,255,255,0.35)' : '#9ca3af',
          }}>
            Auto-redirecting in <span style={{ fontWeight: 700, color: '#14b8a6' }}>{countdown}s</span>
          </p>
        </div>
      </div>
    </div>
  );
}

function PopupRow({ label, value, isDark, border, badge }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0.75rem 1rem',
      borderTop: border ? `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}` : 'none',
    }}>
      <span style={{ fontSize: '0.8125rem', color: isDark ? '#9ca3af' : '#6b7280' }}>{label}</span>
      {badge ? (
        <span style={{
          padding: '0.25rem 0.625rem', borderRadius: '9999px', fontSize: '0.6875rem',
          fontWeight: 700, textTransform: 'uppercase',
          background: 'rgba(20,184,166,0.12)', color: '#14b8a6',
        }}>{value}</span>
      ) : (
        <span style={{ fontSize: '0.875rem', fontWeight: 600, color: isDark ? '#f5f5f7' : '#111827' }}>{value}</span>
      )}
    </div>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin" style={{ width: '1rem', height: '1rem' }} viewBox="0 0 24 24" fill="none">
      <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}
